import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import './Chat.css';
import { AuthContext } from '../../Header/auth - context';
import { useSelector, useDispatch } from 'react-redux';
import { ChatSocketContext } from '../../context/ChatSocketContext';
import { useCall } from '../../context/CallContext';
import LocationPickerModal from './LocationPickerModal';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
/* Snimci poziva zive u Chatu, a ne vise u Administraciji: poziv se i pokrece
   odavde, pa se i snimak trazi tu. Komponenta je ista, samo bez svog okvira —
   modal ispod ima svoje zaglavlje. */
import SnimciPoziva from './SnimciPoziva';
import { useLocation } from 'react-router-dom';
import { applyThemeWithReveal, jeRetro, jeTamna } from '../../theme';
import { Toast } from 'primereact/toast';
import usePermission from '../../custom/usePermission';
import TemaPrekidac from '../../custom/TemaPrekidac';

// ── Helpers ──────────────────────────────────────────────────
const AVATAR_COLORS = ['#5c7cfa','#f76707','#2f9e44','#ae3ec9','#1098ad','#d6336c','#e67700','#0ca678'];
const avatarColor = (name = '') => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const initials    = (name = '') => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

const formatTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const formatDate = (dt) => {
    if (!dt) return '';
    const d   = new Date(dt);
    const now = new Date();
    const dDay   = new Date(d.getFullYear(),   d.getMonth(),   d.getDate()).getTime();
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diff   = Math.floor((nowDay - dDay) / 86400000);
    if (diff === 0) return 'Danas';
    if (diff === 1) return 'Juče';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}.`;
};
const sameDay = (a, b) => {
    const da = new Date(a), db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};
const isImageFile    = (path) => /\.(jpg|jpeg|png|gif|webp)$/i.test(path || '');
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// OPERATERI (auth.Users) i MOBILNI OPERATERI (auth.MobilniNalog) su odvojeni ID prostori —
// ključ oblika "OPERATER:5"/"MOBILNI:5" sprečava kolizije kad se numerički ID poklopi.
const ukey = (tip, id) => `${tip === 'MOBILNI' ? 'MOBILNI' : 'OPERATER'}:${id}`;
const MOB_ROLA_KEY = { 1: 'admin.rolaDirektorKontrolor', 5: 'admin.rolaDispecer', 6: 'admin.rolaVozac', 8: 'admin.rolaServiser' };

const URL_RE = /(https?:\/\/[^\s]+)/g;
const isUrl  = (s) => /^https?:\/\//i.test(s);

const EMOJI_SHORTCUTS = [
    [/<3/g,       '❤️'],
    [/>:-?\(/g,   '😠'],
    [/:-?'\(/g,   '😭'],
    [/:-?\)/g,    '😊'],
    [/:-?\(/g,    '😢'],
    [/:-?D/g,     '😄'],
    [/[Xx]-?D/g,  '😂'],
    [/:-?P/gi,    '😛'],
    [/;-?P/gi,    '😜'],
    [/;-?\)/g,    '😉'],
    [/:-?O/gi,    '😮'],
    [/:-?\*/g,    '😘'],
    [/B-?\)/g,    '😎'],
    [/:-?\//g,    '😕'],
];
/* Emotikoni se zamenjuju SAMO van adresa.
   Bez ovoga `:/` iz `https://` postane 😕 još pri slanju, pa se u bazu upiše
   pokvarena adresa: korisnik vidi „https😕/play.google.com/…" kao običan tekst,
   a renderText je više ne prepozna kao link. Adresa se zato preskače u celosti.

   Deli se istim izrazom kojim renderText linkuje, pa se granice poklapaju. */
const convertShortcuts = (text) =>
    text.split(URL_RE).map(deo =>
        isUrl(deo)
            ? deo
            : EMOJI_SHORTCUTS.reduce((t, [re, emoji]) => t.replace(re, emoji), deo)
    ).join('');
// Oboji sve pojave upita `query` unutar običnog teksta (case-insensitive, i unutar reči)
const highlightParts = (text, query, isCurrent, keyPrefix) => {
    const q = (query || '').trim();
    if (!q) return text;
    const lower = text.toLowerCase();
    const lq = q.toLowerCase();
    const out = [];
    let from = 0, idx, k = 0;
    while ((idx = lower.indexOf(lq, from)) !== -1) {
        if (idx > from) out.push(text.slice(from, idx));
        out.push(
            <mark key={`${keyPrefix}-h${k++}`} className={`chat-search-hit${isCurrent ? ' current' : ''}`}>
                {text.slice(idx, idx + q.length)}
            </mark>
        );
        from = idx + q.length;
    }
    if (from < text.length) out.push(text.slice(from));
    return out;
};
const renderText = (text, query = '', isCurrent = false) => {
    if (!text) return null;
    return text.split(URL_RE).map((part, i) =>
        isUrl(part)
            ? <a key={i} href={part} target="_blank" rel="noreferrer noopener" className="chat-link" onClick={e => e.stopPropagation()}>{part}</a>
            : <React.Fragment key={i}>{highlightParts(part, query, isCurrent, i)}</React.Fragment>
    );
};
const fileUrl = (path) => path
    ? process.env.REACT_APP_BACKEND_URL.replace(/\/API\/?$/, '') + path
    : '';

// ── Avatar (image if set, otherwise colored initials / group icon) ──
const ChatAvatar = ({ name = '', avatar = null, online = false, isGroup = false, size = 46, style = {}, onExpand = null }) => {
    /* online: false/null = bez tačke, true ili 'ONLINE' = zelena, 'AWAY' = žuta. */
    const cls = `chat-avatar${online ? ' chat-avatar-online' : ''}${online === 'AWAY' ? ' chat-avatar-away' : ''}`;
    const dim = { width: size, height: size, fontSize: Math.round(size * 0.4) };
    if (avatar) {
        const clickable = typeof onExpand === 'function';
        return (
            <div className={cls} style={{ ...dim, ...style, background: 'transparent' }}>
                <img
                    src={fileUrl(avatar)}
                    alt={name}
                    className={`chat-avatar-img${clickable ? ' clickable' : ''}`}
                    onClick={clickable ? (e) => { e.stopPropagation(); onExpand(fileUrl(avatar)); } : undefined}
                    title={clickable ? 'Uvećaj sliku' : undefined}
                />
            </div>
        );
    }
    return (
        <div className={cls} style={{ ...dim, ...style, background: isGroup ? '#6b7280' : avatarColor(name) }}>
            {isGroup ? <i className="pi pi-users" style={{ fontSize: Math.round(size * 0.42) }} /> : initials(name)}
        </div>
    );
};

// ── Component ─────────────────────────────────────────────────
const Chat = () => {
    const { t }    = useTranslation();
    const routerLokacija = useLocation();
    const auth     = useContext(AuthContext);
    const dispatch = useDispatch();
    const { socketRef, socket: aktivniSocket, onlineUsers, prisustvo } = useContext(ChatSocketContext);
    const { startCall } = useCall() || {};
    const user     = useSelector(s => s.user);

    const [conversations,  setConversations]  = useState([]);
    const [activeConv,     setActiveConv]     = useState(null);
    const activeConvRef = useRef(null);
    useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);
    const [messages,       setMessages]       = useState([]);
    const [convSearch,     setConvSearch]     = useState('');
    const [showNewModal,   setShowNewModal]   = useState(false);
    const [showEmoji,      setShowEmoji]      = useState(false);
    const [typingUsers,    setTypingUsers]    = useState({});
    const [imgPreview,     setImgPreview]     = useState(null);
    const [previewIndex,   setPreviewIndex]   = useState(null);
    /* Uvećanje otvorene slike. `pomak` je pomeranje prevlačenjem — bez njega je
       uvećanje beskorisno, jer se vidi samo sredina. */
    const [zum,            setZum]            = useState(1);
    const [pomak,          setPomak]          = useState({ x: 0, y: 0 });
    const prevlacenjeOd = useRef(null);
    const slojSlikeRef  = useRef(null);
    const [companyUsers,   setCompanyUsers]   = useState([]);
    const [modalTab,       setModalTab]       = useState('dm');    // 'dm' | 'group'
    const [modalSearch,    setModalSearch]    = useState('');
    const [modalCategory,  setModalCategory]  = useState('OPERATER');   // 'OPERATER' | 'MOBILNI'
    const [selectedUsers,  setSelectedUsers]  = useState([]);           // niz ukey() stringova
    const [groupName,      setGroupName]      = useState('');
    const [uploadingFile,       setUploadingFile]       = useState(false);
    const [pastedImage,         setPastedImage]         = useState(null); // { file, preview }
    /* Prevlačenje fajlova (kao Viber). `prevuci` = fajl se vuče iznad chata (prikazuju
       se zone za puštanje); `prilozi` = pušteni fajlovi čekaju potvrdu u pregledu.
       Režim za slike: 'kompresovano' (slika, manja i brža) ili 'original' (šalje se
       kao fajl, puna rezolucija). */
    const [prevuci,       setPrevuci]       = useState(null);   // null | { slike: bool }
    const [prilozi,       setPrilozi]       = useState(null);   // { fajlovi: [{ id, file, preview, jeSlika }], rezim }
    const [saljePriloge,  setSaljePriloge]  = useState(false);
    const [groupInfo,           setGroupInfo]           = useState(null);
    const [showGroupPanel,      setShowGroupPanel]      = useState(false);
    const [editingId,           setEditingId]           = useState(null);
    const [editingText,         setEditingText]         = useState('');
    const [, setReactionPickerMsgId] = useState(null);
    /* Meni poruke na desni klik (na telefonu dugi pritisak) — zamenio je dugmad
       koja su se pojavljivala na hover. `punIzbor` je ceo emoji-mart izbor za
       reakciju, kad 6 brzih nije dovoljno. Oba nose poziciju klika (fixed). */
    const [ctxMeni,  setCtxMeni]  = useState(null);   // { msg, x, y }
    const [punIzbor, setPunIzbor] = useState(null);   // { msg, x, y }
    /* Prosleđivanje: poruke koje se prosleđuju (jedna iz menija, ili više iz
       „Izaberi više") + izabrani ciljni razgovori. */
    const [prosledi,        setProsledi]        = useState(null);   // [msg, ...]
    const [prosledjivanjeU, setProsledjivanjeU] = useState([]);     // id-jevi razgovora
    const [prosledPretraga, setProsledPretraga] = useState('');
    /* Zakačene poruke otvorenog razgovora (najviše 3, najnovija prva) i koja se
       trenutno vidi u traci — klik na traku vodi na poruku i prelazi na sledeću. */
    const [zakacene, setZakacene] = useState([]);
    const [zakIndex, setZakIndex] = useState(0);
    /* Zadatak iz poruke — prozor sa tekstom (popunjen iz poruke), izborom kome
       se dodeljuje i prioritetom. Korisnici i prioriteti se učitavaju jednom,
       pri prvom otvaranju, sa istih ruta koje koristi tabla zadataka (home.js). */
    const { can } = usePermission();
    const mozeZadatak = can('TASKOVI', 'edit');
    const toastRef = useRef(null);
    const [zadatak,         setZadatak]         = useState(null);   // { msg, tekst, kome:[], prioritet }
    const [zadKorisnici,    setZadKorisnici]    = useState([]);
    const [zadPrioriteti,   setZadPrioriteti]   = useState([]);
    const [zadPretraga,     setZadPretraga]     = useState('');
    const [zadSalje,        setZadSalje]        = useState(false);
    /* Sačuvane poruke (lična zvezdica) — prozor sa spiskom iz SVIH razgovora. */
    const [sacuvaneOtvoreno, setSacuvaneOtvoreno] = useState(false);
    const [snimciOtvoreno, setSnimciOtvoreno] = useState(false);
    const [sacuvane,         setSacuvane]         = useState([]);
    const [sacuvaneUcitava,  setSacuvaneUcitava]  = useState(false);
    /* Izaberi više — dok je `izbor` niz (makar i prazan), poruke se biraju
       klikom, a traka za pisanje je zamenjena trakom sa radnjama. null = isključeno. */
    const [izbor, setIzbor] = useState(null);   // [idPoruke, ...]
    // dugi pritisak okida iz tajmera — ref vidi svežu vrednost, state iz closure ne bi
    const izborRef = useRef(null);
    useEffect(() => { izborRef.current = izbor; }, [izbor]);
    const [replyingTo,          setReplyingTo]          = useState(null);
    const [highlightMsgId,      setHighlightMsgId]      = useState(null);
    const [hoveredConvId,       setHoveredConvId]       = useState(null);
    const [mobileShowConv,      setMobileShowConv]      = useState(false);
    const [addMemberMode,       setAddMemberMode]       = useState(false);
    const [addMemberSearch,     setAddMemberSearch]     = useState('');
    const [addMemberCategory,   setAddMemberCategory]   = useState('OPERATER');   // 'OPERATER' | 'MOBILNI'
    const [readersPopup,        setReadersPopup]        = useState({ open: false, msgId: null, list: [], nisu: [], loading: false, x: 0, y: 0 });
    const [showAppLauncher,     setShowAppLauncher]     = useState(false);
    const [showGifModal,        setShowGifModal]        = useState(false);
    const [gifSearch,           setGifSearch]           = useState('');
    const [gifResults,          setGifResults]          = useState([]);
    const [gifLoading,          setGifLoading]          = useState(false);
    const [showPollModal,       setShowPollModal]       = useState(false);
    const [showLocationModal,   setShowLocationModal]   = useState(false);
    const [pollQuestion,        setPollQuestion]        = useState('');
    const [pollOptions,         setPollOptions]         = useState(['', '']);
    const [showEventModal,      setShowEventModal]      = useState(false);
    const [eventNaziv,          setEventNaziv]          = useState('');
    const [eventDatum,          setEventDatum]          = useState('');
    const [eventOpis,           setEventOpis]           = useState('');
    const [showSettings,        setShowSettings]        = useState(false);
    const globalTheme = useSelector(s => s.theme || 'light');
    // Chat prati globalnu temu (menja se i čuva sa korisnikom). Lokalni toggle u chatu ostaje samo za chat sesiju.
    const [theme,               setTheme]               = useState(() => globalTheme);
    const [myAvatar,            setMyAvatar]            = useState(null);
    const [myName,              setMyName]              = useState('');
    const [avatarUploading,     setAvatarUploading]     = useState(false);
    const [groupAvatarUploading, setGroupAvatarUploading] = useState(false);
    const [groupNameEditing,    setGroupNameEditing]    = useState(false);
    const [groupNameInput,      setGroupNameInput]      = useState('');
    const [showSearch,          setShowSearch]          = useState(false);
    const [searchQuery,         setSearchQuery]         = useState('');
    const [searchMatches,       setSearchMatches]       = useState([]);   // id-jevi poruka koje sadrže upit
    const [currentMatch,        setCurrentMatch]        = useState(0);     // 1-based indeks u searchMatches
    const [showInfoPanel,       setShowInfoPanel]       = useState(false);
    const [infoData,            setInfoData]            = useState(null);
    const [showMediaGallery,    setShowMediaGallery]    = useState(false);
    const [showScrollDown,      setShowScrollDown]      = useState(false);
    /* Prepiska se ne učitava cela. Prva stranica su poruke iz poslednjih
       `DANA_PRVA_STRANICA` dana RAČUNATO OD POSLEDNJE PORUKE (ne od danas), pa se
       razgovor uvek otvara na svom kraju. Starije dolaze skrolom, stranica po
       stranica. Vidi getMessages u chat-controllers.js. */
    const [imaStarijih,        setImaStarijih]        = useState(false);
    const [pretragaOdsecena,   setPretragaOdsecena]   = useState(false);
    const [ucitavamStarije,    setUcitavamStarije]    = useState(false);
    /* Ogledala stanja u ref-ovima. `onScroll` se okida na svaki piksel, a state se
       sleže tek u sledećem renderu — provera po state-u bi propustila nekoliko
       zahteva pre nego što se `ucitavamStarije` upiše. Isto važi za petlju u
       `scrollToMessage`, koja mora da vidi SVEŽU listu posle svakog dovlačenja. */
    const porukeRef        = useRef([]);
    const imaStarijihRef   = useRef(false);
    const ucitavamRef      = useRef(false);

    const messagesEndRef  = useRef(null);
    const messagesScrollRef = useRef(null);
    const inputRef        = useRef(null);
    const typingTimerRef  = useRef(null);
    const fileInputRef    = useRef(null);
    const cameraInputRef  = useRef(null);
    const gifSearchTimer  = useRef(null);
    const avatarInputRef  = useRef(null);
    const groupAvatarInputRef = useRef(null);
    const searchInputRef  = useRef(null);

    const myId = user?.userId;

    const DANA_PRVA_STRANICA = 7;     // koliko dana unazad od POSLEDNJE poruke
    const MAX_PRVA_STRANICA  = 300;   // gornja granica prve stranice (gust razgovor)
    /* Starije poruke idu u prozorima od po DANA_PRVA_STRANICA dana (ne po broju):
       sidro je poslednja poruka STARIJA od učitanih — pa i posle duge pauze skrol
       gore odmah donese prethodni razgovor. MAX_PRVA_STRANICA je granica i ovde. */
    const OKIDAC_GORE        = 150;   // px od vrha na kojima počinje dovlačenje
    const MAX_SKOKOVA        = 10;    // najviše prolaza pri dovlačenju do tražene poruke

    /* Dovlačenje starijih MENJA visinu liste iznad trenutnog prikaza, pa bi se bez
       ovoga sadržaj odskočio. Pre dovlačenja se pamti rastojanje od DNA (ono se ne
       menja kad se dodaje na vrh), a posle se skrol vrati na njega. */
    const sidroSkrola = useRef(null);
    /* Automatski skrol na dno sme SAMO pri otvaranju razgovora i na novu poruku —
       nikako posle dovlačenja starijih, jer bi korisnika vratio odakle je krenuo. */
    const drziDno = useRef(true);
    /* Pri otvaranju razgovora lista se postavlja na dno ODMAH, ne glatko. Glatki
       skrol kreće od vrha (scrollTop = 0), pa je prvi `scroll` događaj izgledao kao
       "korisnik je gore" i okidao dovlačenje starijih — koje je onda vratilo prikaz
       na to mesto, i razgovor se otvarao na sredini. `spremno` zaključava dovlačenje
       dok početna pozicija nije postavljena. */
    const otvaranjeRef = useRef(false);
    const spremnoRef   = useRef(false);
    /* Da li je korisnik na dnu — samo tada nova poruka ili slika koja se upravo
       učitala sme da ga spusti. Ko čita starije, ostaje gde jeste. */
    const naDnuRef     = useRef(true);

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

    // Prikaži strelicu "na dno" kad korisnik skroluje naviše od dna
    const handleMessagesScroll = () => {
        const el = messagesScrollRef.current;
        if (!el) return;
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        naDnuRef.current = dist < 80;
        setShowScrollDown(dist > 200);
        if (ctxMeni || punIzbor) zatvoriMenije();   // fixed meni bi ostao da visi iznad pogrešne poruke
        if (el.scrollTop < OKIDAC_GORE) ucitajStarije();
    };

    /* Kad je lista već na vrhu (ili je prekratka da bi se skrolovala), točkić i
       prst ne pomeraju `scrollTop`, pa se `onScroll` iznad nikad ne okine. Zato se
       sama namera hvata direktno: točkić naviše / prst povučen nadole, na vrhu. */
    const naVrhu = () => (messagesScrollRef.current?.scrollTop ?? 0) < OKIDAC_GORE;
    const handleMessagesWheel = (e) => {
        if (e.deltaY < 0 && naVrhu()) ucitajStarije();
    };
    const dodirY = useRef(null);
    const handleTouchStart = (e) => { dodirY.current = e.touches?.[0]?.clientY ?? null; };
    const handleTouchMove = (e) => {
        const y = e.touches?.[0]?.clientY;
        if (dodirY.current == null || y == null) return;
        // prst ide NADOLE = sadržaj se vuče ka starijim porukama
        if (y - dodirY.current > 30 && naVrhu()) { dodirY.current = y; ucitajStarije(); }
    };

    const handleErrors = r => { if (r.ok) return r.json(); throw r; };

    const apiFetch = useCallback((endpoint, body) =>
        fetch(process.env.REACT_APP_BACKEND_URL + '/chat' + endpoint, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body:    JSON.stringify(body),
        }).then(handleErrors),
    [auth.token]);

    // ── Load conversations ────────────────────────────────────
    const loadConversations = useCallback(() => {
        apiFetch('/getConversations', {}).then(setConversations).catch(() => {});
    }, [apiFetch]);

    useEffect(() => { loadConversations(); }, []);

    /* Chat prati globalnu temu. Nekadašnji upis u localStorage `chatTheme` je
       obrisan: taj ključ se nigde nije čitao, a prava tema se ionako čuva u
       localStorage.appTheme (theme.js) i na nalogu (auth.Users.tema). */
    useEffect(() => { setTheme(globalTheme); }, [globalTheme]);
    useEffect(() => {
        /* Avatar ide i u store: zaglavlje ga prikazuje pored izbora firme, a
           onaj ko se prijavio pre ove izmene inače ne bi imao šta da vidi dok
           se ponovo ne prijavi. */
        apiFetch('/getMe', {}).then(d => {
            setMyAvatar(d?.avatar || null);
            setMyName(d?.ime || '');
            dispatch({ type: 'AVATAR_SET', value: d?.avatar || null });
        }).catch(() => {});
    }, [apiFetch]);

    // Globalni badge (Sidebar) drži se u sinhronizaciji sa zbirom neprocitano
    // po konverzaciji, umesto INC/DEC bookkeeping-a koji je mogao da se rasinhronizuje
    // (npr. kad je poruka stigla u konverzaciju koja se upravo gleda).
    useEffect(() => {
        const total = conversations.reduce((sum, c) => sum + (c.neprocitano || 0), 0);
        dispatch({ type: 'CHAT_UNREAD_SET', value: total });
    }, [conversations, dispatch]);

    // ── Socket listeners ──────────────────────────────────────
    useEffect(() => {
        /* Zavisi od socket STANJA. Ranije je zavisio od socketRef.current, što je
           radilo samo slučajno — kad neki drugi state promeni izazove ponovni render. */
        const socket = aktivniSocket;
        if (!socket) return;

        const sortConvs = convs => [...convs].sort((a, b) => new Date(b.zadnja_datum) - new Date(a.zadnja_datum));

        const onNewMessage = (msg) => {
            setActiveConv(prev => {
                if (prev && prev.id === msg.id_grupe) {
                    setMessages(m => [...m, msg]);
                    // Ne markiraj kao pročitano dok prozor/tab nije fokusiran —
                    // korisnik fizički nije mogao da vidi poruku.
                    if (document.hasFocus()) {
                        socket.emit('read-messages', { idGrupe: msg.id_grupe });
                        setConversations(convs => sortConvs(convs.map(c =>
                            c.id === msg.id_grupe
                                ? { ...c, neprocitano: 0, zadnja_poruka: msg.sadrzaj, zadnja_datum: msg.datum, zadnja_tip: msg.tip }
                                : c
                        )));
                    } else {
                        setConversations(convs => sortConvs(convs.map(c =>
                            c.id === msg.id_grupe
                                ? { ...c, neprocitano: (c.neprocitano || 0) + 1, zadnja_poruka: msg.sadrzaj, zadnja_datum: msg.datum, zadnja_tip: msg.tip }
                                : c
                        )));
                    }
                } else {
                    setConversations(convs => sortConvs(convs.map(c =>
                        c.id === msg.id_grupe
                            ? { ...c, neprocitano: (c.neprocitano || 0) + 1, zadnja_poruka: msg.sadrzaj, zadnja_datum: msg.datum, zadnja_tip: msg.tip }
                            : c
                    )));
                }
                return prev;
            });
        };

        const onTyping     = ({ userId, userTip, idGrupe, ime }) => {
            if (userId === myId && (userTip || 'OPERATER') === 'OPERATER') return;
            setTypingUsers(t => ({ ...t, [`${idGrupe}_${userId}`]: ime }));
        };
        const onStopTyping = ({ userId, idGrupe }) => {
            setTypingUsers(t => { const n = { ...t }; delete n[`${idGrupe}_${userId}`]; return n; });
        };
        const onRead = ({ userId: uid, userTip: uTip, idGrupe, idPoruke }) => {
            if (uid === myId && (uTip || 'OPERATER') === 'OPERATER') {
                setConversations(convs => convs.map(c =>
                    c.id === idGrupe ? { ...c, neprocitano: 0 } : c
                ));
            } else {
                const idSet = new Set(idPoruke || []);
                setMessages(msgs => msgs.map(m =>
                    idSet.has(m.id)
                        ? { ...m, procitano_count: (m.procitano_count || 0) + 1 }
                        : m
                ));
            }
        };

        const onMessageEdited = ({ id, sadrzaj }) => {
            setMessages(msgs => msgs.map(m => m.id === id ? { ...m, sadrzaj, izmenjeno: 1 } : m));
        };
        const onMessageDeleted = ({ id }) => {
            setMessages(msgs => msgs.map(m =>
                m.id === id ? { ...m, obrisan: 1, sadrzaj: null, file_path: null, file_naziv: null, reakcije: [] } : m
            ));
        };
        const onMessageReaction = ({ idPoruke, userId: uid, userTip: uTip, emoji, action }) => {
            const isMe = uid === myId && (uTip || 'OPERATER') === 'OPERATER';
            setMessages(msgs => msgs.map(m => {
                if (m.id !== idPoruke) return m;
                const rList = [...(m.reakcije || [])];
                const idx   = rList.findIndex(r => r.emoji === emoji);
                if (action === 'add') {
                    if (idx >= 0) rList[idx] = { ...rList[idx], cnt: rList[idx].cnt + 1, mine: isMe ? 1 : rList[idx].mine };
                    else          rList.push({ emoji, cnt: 1, mine: isMe ? 1 : 0 });
                } else if (idx >= 0) {
                    const nc = rList[idx].cnt - 1;
                    if (nc <= 0) rList.splice(idx, 1);
                    else rList[idx] = { ...rList[idx], cnt: nc, mine: isMe ? 0 : rList[idx].mine };
                }
                return { ...m, reakcije: rList };
            }));
        };

        const onPollVoted = ({ idPoruke, voterUserId, voterTip, idOpcije, opcijeCounts, ukupnoGlasova }) => {
            setMessages(msgs => msgs.map(m => {
                if (m.id !== idPoruke || !m.poll_data) return m;
                const amIVoter = voterUserId === myId && (voterTip || 'OPERATER') === 'OPERATER';
                const updatedOpcije = m.poll_data.opcije.map(o => ({
                    ...o,
                    glasova:   opcijeCounts.find(c => c.id === o.id)?.glasova ?? o.glasova,
                    ja_glasao: amIVoter ? (o.id === idOpcije ? 1 : 0) : o.ja_glasao,
                }));
                return { ...m, poll_data: { ...m.poll_data, opcije: updatedOpcije, ukupno_glasova: ukupnoGlasova } };
            }));
        };

        // Promena članstva grupe (dodavanje/uklanjanje/napuštanje) — osveži uživo
        const onGroupUpdated = ({ idGrupe }) => {
            loadConversations();
            setActiveConv(prev => {
                if (prev && prev.id === idGrupe) {
                    apiFetch('/getGroupInfo', { idGrupe }).then(info => setGroupInfo(info)).catch(() => {});
                }
                return prev;
            });
        };

        // Tek si dodat u grupu — uđi u sobu (za live poruke) i osveži listu chatova
        const onAddedToGroup = ({ idGrupe }) => {
            socket.emit('join-room', { idGrupe });
            loadConversations();
        };

        /* Zakači/otkači/obriši — server šalje ceo svež spisak za razgovor. */
        const onPinsUpdated = ({ idGrupe, zakacene: lista }) => {
            if (activeConvRef.current?.id !== idGrupe) return;
            setZakacene(Array.isArray(lista) ? lista : []);
            setZakIndex(0);
        };
        socket.on('pins-updated',     onPinsUpdated);

        socket.on('new-message',      onNewMessage);
        socket.on('group-updated',    onGroupUpdated);
        socket.on('added-to-group',   onAddedToGroup);
        socket.on('typing',           onTyping);
        socket.on('stop-typing',      onStopTyping);
        socket.on('messages-read',    onRead);
        socket.on('message-edited',   onMessageEdited);
        socket.on('message-reaction', onMessageReaction);
        socket.on('message-deleted',  onMessageDeleted);
        socket.on('poll-voted',       onPollVoted);

        return () => {
            socket.off('pins-updated',     onPinsUpdated);
            socket.off('new-message',      onNewMessage);
            socket.off('group-updated',    onGroupUpdated);
            socket.off('added-to-group',   onAddedToGroup);
            socket.off('typing',           onTyping);
            socket.off('stop-typing',      onStopTyping);
            socket.off('messages-read',    onRead);
            socket.off('message-edited',   onMessageEdited);
            socket.off('message-reaction', onMessageReaction);
            socket.off('message-deleted',  onMessageDeleted);
            socket.off('poll-voted',       onPollVoted);
        };
    }, [aktivniSocket, myId]);

    useEffect(() => { porukeRef.current = messages; }, [messages]);
    useEffect(() => { imaStarijihRef.current = imaStarijih; }, [imaStarijih]);

    useEffect(() => {
        const el = messagesScrollRef.current;

        /* 1) Tek otvoren razgovor: odmah na dno, pa tek onda dozvoli dovlačenje. */
        if (otvaranjeRef.current) {
            if (el) el.scrollTop = el.scrollHeight;
            otvaranjeRef.current = false;
            naDnuRef.current = true;
            drziDno.current = true;
            sidroSkrola.current = null;
            requestAnimationFrame(() => { spremnoRef.current = true; });
            return;
        }

        /* 2) Dodate starije: vrati prikaz tamo gde je korisnik bio. Rastojanje od
              dna je stabilno merilo — visina liste je upravo porasla. */
        if (!drziDno.current) {
            if (el && sidroSkrola.current != null) el.scrollTop = el.scrollHeight - sidroSkrola.current;
            sidroSkrola.current = null;
            drziDno.current = true;
            return;
        }

        /* 3) Nova poruka ili izmena (reakcija, brisanje, čitanje): na dno SAMO ako
              je korisnik već bio dole ili je poruka njegova. Ranije je svaka
              reakcija vraćala na dno i onoga ko čita starije. */
        const zadnja = messages[messages.length - 1];
        const moja = zadnja && zadnja.id_korisnik === myId && (zadnja.tip_korisnika || 'OPERATER') === 'OPERATER';
        if (naDnuRef.current || moja) scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    /* Slika (ili GIF) koja se učita POSLE pozicioniranja poveća visinu liste, pa
       bi poslednja poruka ostala ispod ekrana. Ako je korisnik bio na dnu, drži
       ga tamo. `load` ne bubla, zato hvatanje u capture fazi na kontejneru. */
    useEffect(() => {
        const el = messagesScrollRef.current;
        if (!el) return undefined;
        const naUcitano = (e) => {
            if (e.target?.tagName === 'IMG' && naDnuRef.current) el.scrollTop = el.scrollHeight;
        };
        el.addEventListener('load', naUcitano, true);
        return () => el.removeEventListener('load', naUcitano, true);
    }, [activeConv?.id]);

    /* Prva stranica (7 dana) često ne popuni ekran — tada nema skrola, pa ni
       okidača za starije. Dok lista nije viša od prozora a starijih ima, dovlači
       se sama, stranicu po stranicu. Meri se posle iscrtavanja (rAF), a petlja se
       gasi sama: `ucitajStarije` ima bravu, a `imaStarijih` padne na false. */
    useEffect(() => {
        if (!imaStarijih || ucitavamStarije) return undefined;
        const raf = requestAnimationFrame(() => {
            const el = messagesScrollRef.current;
            if (el && el.scrollHeight <= el.clientHeight + OKIDAC_GORE) ucitajStarije();
        });
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, imaStarijih, ucitavamStarije]);

    // ── Pretraga unutar razgovora ─────────────────────────────
    const currentMatchId = currentMatch > 0 ? searchMatches[currentMatch - 1] : null;

    /* Pretraga ide U BAZU, nad CELOM istorijom razgovora.

       Ranije je filtrirala `messages` — učitane poruke. Od kad se prepiska učitava po
       stranicama to bi značilo da pretraga ne vidi istoriju, a istorija je upravo ono
       zbog čega se pretraga i otvara. Backend vraća samo `id` i `datum` pogodaka, pa
       je i nekoliko stotina pogodaka mali odgovor; tekst se već crta iz učitanih poruka,
       a poruka koja nije učitana dovuče se pri skoku na nju (`scrollToMessage`).

       Kucanje se odlaže (`ODLAGANJE_PRETRAGE`) da svaki pritisnut taster ne pošalje
       upit; zahtev koji je u međuvremenu zastario odbacuje se po `traziRef`. */
    const ODLAGANJE_PRETRAGE = 300;
    const traziRef = useRef(0);

    useEffect(() => {
        const q = searchQuery.trim();
        const conv = activeConv;
        if (!q || !conv) { setSearchMatches([]); setCurrentMatch(0); setPretragaOdsecena(false); return; }

        const moj = ++traziRef.current;
        const tajmer = setTimeout(async () => {
            try {
                const odg = await apiFetch('/searchMessages', { idGrupe: conv.id, q });
                if (moj !== traziRef.current) return;      // stigao stariji odgovor
                const ids = (odg?.pogodci || []).map(x => x.id);
                setSearchMatches(ids);
                setCurrentMatch(ids.length ? ids.length : 0);   // počni od NAJNOVIJEG pogotka
                setPretragaOdsecena(!!odg?.odsecen);
            } catch (err) {
                if (moj !== traziRef.current) return;
                console.error('searchMessages failed:', err);
                setSearchMatches([]); setCurrentMatch(0); setPretragaOdsecena(false);
            }
        }, ODLAGANJE_PRETRAGE);
        return () => clearTimeout(tajmer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, activeConv?.id]);

    /* Skok na tekući pogodak. Poruka može biti starija od učitanog prozora — tada
       `scrollToMessage` prvo dovuče sve do nje. */
    useEffect(() => {
        if (currentMatchId) scrollToMessage(currentMatchId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMatchId]);

    const openSearch  = () => { setShowSearch(true); setTimeout(() => searchInputRef.current?.focus(), 50); };
    const closeSearch = () => { setShowSearch(false); setSearchQuery(''); setSearchMatches([]); setCurrentMatch(0); setPretragaOdsecena(false); };
    const gotoMatch = (where) => {
        const n = searchMatches.length;
        if (!n) return;
        setCurrentMatch(prev => {
            if (where === 'first') return 1;
            if (where === 'last')  return n;
            if (where === 'next')  return prev >= n ? 1 : prev + 1;
            if (where === 'prev')  return prev <= 1 ? n : prev - 1;
            return prev;
        });
    };

    // ── Info panel (datum, učesnici, media) ───────────────────
    const openInfoPanel = async () => {
        if (!activeConv) return;
        setShowMediaGallery(false);
        setShowInfoPanel(true);
        try {
            const info = await apiFetch('/getGroupInfo', { idGrupe: activeConv.id }); // radi i za DM
            setInfoData(info);
        } catch { setInfoData(null); }
    };
    const closeInfoPanel = () => { setShowInfoPanel(false); setShowMediaGallery(false); };

    // ── Markiraj otvoren razgovor kao pročitan tek kad prozor dobije fokus ──
    useEffect(() => {
        const onFocus = () => {
            const conv = activeConvRef.current;
            if (!conv) return;
            socketRef.current?.emit('read-messages', { idGrupe: conv.id });
            setConversations(convs => convs.map(c => c.id === conv.id ? { ...c, neprocitano: 0 } : c));
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [socketRef]);

    // ── Open conversation ─────────────────────────────────────
    const openConversation = async (conv) => {
        setActiveConv(conv);
        setMobileShowConv(true);
        setShowEmoji(false);
        setReplyingTo(null);
        setShowSearch(false);
        setSearchQuery('');
        setShowInfoPanel(false);
        setShowMediaGallery(false);
        setConversations(convs => convs.map(c => c.id === conv.id ? { ...c, neprocitano: 0 } : c));
        setImaStarijih(false);
        setUcitavamStarije(false);
        imaStarijihRef.current = false;
        ucitavamRef.current = false;
        porukeRef.current = [];
        sidroSkrola.current = null;
        drziDno.current = true;          // novi razgovor se uvek otvara na poslednjoj poruci
        otvaranjeRef.current = true;     // sledeći setMessages: skok na dno, ne glatko
        spremnoRef.current = false;      // bez dovlačenja starijih dok se ne pozicionira
        setZakacene([]);
        setZakIndex(0);
        setIzbor(null);                  // izbor važi samo za razgovor u kome je počet
        apiFetch('/getPinned', { idGrupe: conv.id })
            .then(z => { if (activeConvRef.current?.id === conv.id) setZakacene(Array.isArray(z) ? z : []); })
            .catch(() => {});
        try {
            /* `straniceno` traži objekat sa `imaStarijih`; bez njega backend vraća
               goli niz (tako radi mobilni klijent i tako je radilo pre). */
            const odg = await apiFetch('/getMessages', {
                idGrupe: conv.id,
                dana: DANA_PRVA_STRANICA,
                maxPoruka: MAX_PRVA_STRANICA,
                straniceno: 1,
            });
            /* Tolerancija na oba oblika — ako backend još nije osvežen, ne pada ekran. */
            const msgs = Array.isArray(odg) ? odg : (odg?.poruke || []);
            setMessages(msgs);
            porukeRef.current = msgs;
            const ima = Array.isArray(odg) ? false : !!odg?.imaStarijih;
            imaStarijihRef.current = ima;
            setImaStarijih(ima);
            socketRef.current?.emit('read-messages', { idGrupe: conv.id });
        } catch (err) {
            console.error('getMessages failed:', err);
            setMessages([]);
        }
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    /* Dovuci jednu stranicu STARIJIH poruka i nalepi je na vrh.
       Čuva se od dvostrukog poziva: `onScroll` se okida na svaki pomeraj piksela. */
    const ucitajStarije = useCallback(async () => {
        const conv = activeConvRef.current;
        if (!conv || ucitavamRef.current || !imaStarijihRef.current || !spremnoRef.current) return;
        const najstariji = porukeRef.current[0]?.id;
        if (!najstariji) return;

        /* Ref se zatvara ODMAH — state tek u sledećem renderu, a dotad bi skrol
           poslao još nekoliko istih zahteva. */
        ucitavamRef.current = true;
        setUcitavamStarije(true);
        const el = messagesScrollRef.current;
        /* rastojanje od dna — jedino merilo koje dodavanje na vrh ne pomera */
        sidroSkrola.current = el ? el.scrollHeight - el.scrollTop : null;
        drziDno.current = false;
        try {
            const odg = await apiFetch('/getMessages', {
                idGrupe: conv.id, preId: najstariji, dana: DANA_PRVA_STRANICA,
                maxPoruka: MAX_PRVA_STRANICA, straniceno: 1,
            });
            const stare = Array.isArray(odg) ? odg : (odg?.poruke || []);
            if (stare.length) {
                /* Zaštita od duplikata: ista poruka može doći dvaput ako se u
                   međuvremenu nešto promenilo oko granice stranice. */
                setMessages(curr => {
                    const imam = new Set(curr.map(m => m.id));
                    const nove = stare.filter(m => !imam.has(m.id));
                    return nove.length ? [...nove, ...curr] : curr;
                });
            } else {
                drziDno.current = true;     // nema šta da se vrati, ne diraj skrol
                sidroSkrola.current = null;
            }
            const ima = Array.isArray(odg) ? stare.length > 0 : !!odg?.imaStarijih;
            imaStarijihRef.current = ima;
            setImaStarijih(ima);
        } catch (err) {
            console.error('getMessages (starije) failed:', err);
            drziDno.current = true;
            sidroSkrola.current = null;
        }
        ucitavamRef.current = false;
        setUcitavamStarije(false);
    }, [apiFetch]);

    /* Dovuci SVE do poruke `id` i ubaci je u listu, ako već nije učitana.

       Praznina se puni odozgo nadole (backend vraća najnovije iz raspona), pa lista
       ostaje NEPREKIDNA — da se poruka ubaci kao samostalan komad, u nizu bi nastala
       rupa i "učitaj starije" bi od tog trenutka preskakao sve između.

       Vraća true ako je poruka na kraju u listi. Petlja postoji jer backend seče
       prazninu na `MAX_PRVA_STRANICA`, pa veliki raspon traži više prolaza. */
    const popuniDo = useCallback(async (id) => {
        const conv = activeConvRef.current;
        if (!conv || !id) return false;
        const imam = () => porukeRef.current.some(m => String(m.id) === String(id));
        if (imam()) return true;

        for (let i = 0; i < MAX_SKOKOVA; i++) {
            const preId = porukeRef.current[0]?.id;
            if (!preId) return false;
            /* Poruka nije starija od učitanog prozora, a nije ni u njemu — obrisana je
               ili više ne pripada razgovoru. Dalje dovlačenje ne bi pomoglo. */
            if (Number(id) >= Number(preId)) return false;

            setUcitavamStarije(true);
            ucitavamRef.current = true;
            drziDno.current = false;       // ne skači na dno posle dodavanja
            sidroSkrola.current = null;    // skrol ide na nađenu poruku, ne na staro mesto
            let stare = [];
            try {
                /* maxPoruka MORA da ide: bez njega je server vraćao po 1 poruku, pa
                   skok na pogodak stariji od ~10 poruka nije stizao do cilja. */
                const odg = await apiFetch('/getMessages', {
                    idGrupe: conv.id, odId: id, preId, maxPoruka: MAX_PRVA_STRANICA, straniceno: 1,
                });
                stare = Array.isArray(odg) ? odg : (odg?.poruke || []);
            } catch (err) {
                console.error('getMessages (praznina) failed:', err);
            }
            ucitavamRef.current = false;
            setUcitavamStarije(false);
            if (!stare.length) return false;

            const poznate = new Set(porukeRef.current.map(m => m.id));
            const nove = stare.filter(m => !poznate.has(m.id));
            if (!nove.length) return imam();
            const spojeno = [...nove, ...porukeRef.current];
            porukeRef.current = spojeno;   // petlja mora da vidi svežu listu ODMAH
            setMessages(spojeno);
            if (imam()) {
                /* Posle popunjavanja starijih svakako ima — poruka je bila ispod prozora. */
                imaStarijihRef.current = true;
                setImaStarijih(true);
                return true;
            }
        }
        return imam();
    }, [apiFetch]);

    /* Dolazak sa druge stranice sa vec izabranim sagovornikom — GPS mapa salje
       vozaca kroz stanje rute (history.push('/chat', { otvoriRazgovor })).

       Ceka se da konverzacije stignu, jer se prvo trazi POSTOJECI razgovor;
       tek ako ga nema, otvara se prozor za novi sa vec cekiranim covekom. Bez
       tog reda bi se svaki dolazak sa mape zavrsavao praznim novim razgovorom
       pored vec postojeceg.

       Zahtev se trosi jednom (ref se prazni) — inace bi se prozor vracao pri
       svakom osvezavanju liste konverzacija. */
    const zahtevRazgovora = useRef(routerLokacija.state?.otvoriRazgovor || null);
    useEffect(() => {
        const z = zahtevRazgovora.current;
        if (!z || !z.id || !conversations.length) return;
        zahtevRazgovora.current = null;

        const tip = z.tip === 'MOBILNI' ? 'MOBILNI' : 'OPERATER';
        const postojeci = conversations.find(c =>
            c.tip === 1
            && String(c.dm_user_id) === String(z.id)
            && (c.dm_user_tip || 'OPERATER') === tip);

        if (postojeci) { openConversation(postojeci); return; }

        setModalTab('dm');
        setModalCategory(tip);
        setSelectedUsers([ukey(tip, z.id)]);
        setShowNewModal(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversations]);

    const markConvAsUnread = async (e, conv) => {
        e.stopPropagation();
        try {
            await apiFetch('/markAsUnread', { idGrupe: conv.id });
            setConversations(convs => convs.map(c =>
                c.id === conv.id ? { ...c, neprocitano: 1 } : c
            ));
        } catch {}
    };

    // ── Group info panel ─────────────────────────────────────
    const openGroupPanel = async () => {
        if (!activeConv || activeConv.tip !== 2) return;
        try {
            const info = await apiFetch('/getGroupInfo', { idGrupe: activeConv.id });
            setGroupInfo(info);
            setShowGroupPanel(true);
            setAddMemberMode(false);
            setAddMemberSearch('');
            // uvek sveže — vidi openNewChatModal
            const users = await apiFetch('/getCompanyUsers', {});
            setCompanyUsers(users);
        } catch {}
    };

    const closeGroupPanel = () => {
        setShowGroupPanel(false);
        setAddMemberMode(false);
        setAddMemberSearch('');
    };

    const addGroupMember = async (targetUserId, targetTip) => {
        try {
            await apiFetch('/addGroupMember', { idGrupe: activeConv.id, targetUserId, targetTip });
            const info = await apiFetch('/getGroupInfo', { idGrupe: activeConv.id });
            setGroupInfo(info);
            const newCount = info.clanovi.length;
            setConversations(convs => convs.map(c => c.id === activeConv.id ? { ...c, broj_clanova: newCount } : c));
            setActiveConv(prev => prev ? { ...prev, broj_clanova: newCount } : prev);
            setAddMemberMode(false);
            setAddMemberSearch('');
        } catch {}
    };

    const removeGroupMember = async (targetUserId, targetTip) => {
        try {
            await apiFetch('/removeGroupMember', { idGrupe: activeConv.id, targetUserId, targetTip });
            const info = await apiFetch('/getGroupInfo', { idGrupe: activeConv.id });
            setGroupInfo(info);
            const newCount = info.clanovi.length;
            setConversations(convs => convs.map(c => c.id === activeConv.id ? { ...c, broj_clanova: newCount } : c));
            setActiveConv(prev => prev ? { ...prev, broj_clanova: newCount } : prev);
        } catch {}
    };

    // ── Send message ──────────────────────────────────────────
    const sendMessage = async () => {
        if (!activeConv || !socketRef.current) return;
        const text = inputRef.current?.innerText?.trim();
        if (!text && !pastedImage) return;

        const replyToId = replyingTo?.id || null;

        if (pastedImage) {
            setUploadingFile(true);
            const fd = new FormData();
            fd.append('file', pastedImage.file, `paste_${Date.now()}.png`);
            try {
                const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/chat/uploadFile', {
                    method:  'POST',
                    headers: { Authorization: 'Bearer ' + auth.token },
                    body:    fd,
                }).then(r => { if (r.ok) return r.json(); throw r; });
                socketRef.current.emit('send-message', {
                    idGrupe:   activeConv.id,
                    sadrzaj:   null,
                    tip:       'image',
                    filePath:  res.path,
                    fileNaziv: res.naziv,
                    replyToId,
                });
            } catch {}
            setUploadingFile(false);
            removePastedImage();
        }

        if (text) {
            socketRef.current.emit('send-message', {
                idGrupe: activeConv.id,
                sadrzaj: convertShortcuts(text),
                tip:     'text',
                replyToId,
            });
            inputRef.current.innerText = '';
        }
        setReplyingTo(null);
        setShowEmoji(false);
    };

    // ── Reply ─────────────────────────────────────────────────
    /* Kopiranje teksta poruke. Nema toasta u chatu, pa potvrda ide na samo dugme:
       ikonica se na trenutak pretvori u kvačicu. */
    const [kopiranId, setKopiranId] = useState(null);
    const kopirajPoruku = (msg) => {
        const tekst = msg?.sadrzaj || '';
        if (!tekst) return;
        const gotovo = () => { setKopiranId(msg.id); setTimeout(() => setKopiranId(k => (k === msg.id ? null : k)), 1500); };
        /* Rezerva: http bez sigurnog konteksta, stariji pregledač ili odbijena dozvola. */
        const staro = () => {
            const ta = document.createElement('textarea');
            ta.value = tekst; ta.style.position = 'fixed'; ta.style.opacity = '0';
            document.body.appendChild(ta); ta.select();
            try { if (document.execCommand('copy')) gotovo(); } catch {}
            document.body.removeChild(ta);
        };
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(tekst).then(gotovo).catch(staro);
        else staro();
    };

    const startReply = (msg) => {
        setReplyingTo(msg);
        setReactionPickerMsgId(null);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    /* Klik na citat (reply preview), ili skok na pogodak pretrage, odvodi i markira
       poruku. Od kad se prepiska učitava po stranicama poruka može biti starija od
       učitanog prozora — tada je nema u DOM-u i klik bi tiho ništa ne radio, pa se
       prvo dovuče sve do nje. */
    const scrollToMessage = useCallback(async (id) => {
        if (!id) return;
        const nadji = () => document.getElementById(`chat-msg-${id}`);
        if (!nadji()) {
            if (!(await popuniDo(id))) return;
            /* React mora da iscrta dodate poruke pre nego što ih tražimo u DOM-u. */
            await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        }
        const el = nadji();
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightMsgId(id);
        setTimeout(() => setHighlightMsgId(curr => curr === id ? null : curr), 1500);
    }, [popuniDo]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // ── Typing indicator ──────────────────────────────────────
    const handleInput = () => {
        if (!activeConv || !socketRef.current) return;
        socketRef.current.emit('typing', { idGrupe: activeConv.id });
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
            socketRef.current?.emit('stop-typing', { idGrupe: activeConv.id });
        }, 2000);
    };

    // ── Paste image ──────────────────────────────────────────
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (!file) return;
                if (pastedImage) URL.revokeObjectURL(pastedImage.preview);
                setPastedImage({ file, preview: URL.createObjectURL(file) });
                return;
            }
        }
    };

    const removePastedImage = () => {
        if (pastedImage) URL.revokeObjectURL(pastedImage.preview);
        setPastedImage(null);
    };

    // ── Prevlačenje fajlova (drag & drop) ────────────────────────
    const MAX_PRILOGA     = 10;
    const MAX_VELICINA_MB = 10;               // ista granica kao multer na serveru (chatUpload)
    const prevuciBrojac   = useRef(0);        // dragenter/dragleave se okidaju i za decu — broji se
    const otvoriPrevucen  = useRef(null);     // tajmer: razgovor iz liste se otvara kad se fajl zadrži nad njim

    const imaFajlove = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const imaSlike = (e) => Array.from(e.dataTransfer?.items || [])
        .some(it => it.kind === 'file' && /^image\//.test(it.type));

    const naPrevlacenjeUlaz = (e) => {
        if (!imaFajlove(e)) return;
        e.preventDefault();
        prevuciBrojac.current += 1;
        setPrevuci({ slike: imaSlike(e) });
    };
    const naPrevlacenjeIznad = (e) => {
        if (!imaFajlove(e)) return;
        e.preventDefault();                   // bez ovoga pregledač ne dozvoljava drop
        e.dataTransfer.dropEffect = 'copy';
    };
    /* Aktivna zona se prati kroz stanje — :hover se tokom prevlačenja ne okida.
       Stanje se menja samo kad se zona promeni (dragover se okida stalno). */
    const nadZonom = (e, zona) => {
        if (e) naPrevlacenjeIznad(e);
        setPrevuci(p => (p && p.zona !== zona ? { ...p, zona } : p));
    };
    const naPrevlacenjeIzlaz = (e) => {
        if (!imaFajlove(e)) return;
        prevuciBrojac.current = Math.max(0, prevuciBrojac.current - 1);
        if (prevuciBrojac.current === 0) setPrevuci(null);
    };
    /* Pušteno bilo gde u chatu van zona — tretira se kao podrazumevana zona
       (slike kompresovano). Bez ovoga bi pregledač otvorio fajl u novoj kartici. */
    const naPustanje = (e, rezim = 'kompresovano') => {
        if (!imaFajlove(e)) return;
        e.preventDefault();
        e.stopPropagation();
        prevuciBrojac.current = 0;
        setPrevuci(null);
        clearTimeout(otvoriPrevucen.current);
        if (!activeConvRef.current) return;   // nema otvorenog razgovora — nema kome
        primiFajlove(Array.from(e.dataTransfer.files || []), rezim);
    };

    /* Fajl zadržan nad razgovorom u listi pola sekunde → razgovor se otvara, pa
       se pusti nad porukama (kao Viber). */
    const prevucenNadRazgovor = (e, conv) => {
        if (!imaFajlove(e)) return;
        e.preventDefault();
        if (activeConvRef.current?.id === conv.id || otvoriPrevucen.current?.convId === conv.id) return;
        clearTimeout(otvoriPrevucen.current);
        const t = setTimeout(() => { otvoriPrevucen.current = null; openConversation(conv); }, 500);
        t.convId = conv.id;
        otvoriPrevucen.current = t;
    };

    const primiFajlove = (lista, rezim) => {
        const fajlovi = lista.slice(0, MAX_PRILOGA).map((file, i) => {
            const jeSlika = /^image\//.test(file.type);
            return { id: `${Date.now()}-${i}`, file, jeSlika, preview: jeSlika ? URL.createObjectURL(file) : null };
        });
        if (!fajlovi.length) return;
        setPrilozi(prev => {
            /* Drugo puštanje dok je pregled otvoren dodaje na postojeće. */
            const ranije = prev?.fajlovi || [];
            return { rezim: prev?.rezim || rezim, fajlovi: [...ranije, ...fajlovi].slice(0, MAX_PRILOGA) };
        });
        if (lista.length > MAX_PRILOGA) {
            toastRef.current?.show({ severity: 'warn', summary: t('chat.prilozi'), detail: t('chat.prilogaNajvise', { broj: MAX_PRILOGA }), life: 3500 });
        }
    };

    const ukloniPrilog = (id) => setPrilozi(p => {
        if (!p) return p;
        const zaBrisanje = p.fajlovi.find(f => f.id === id);
        if (zaBrisanje?.preview) URL.revokeObjectURL(zaBrisanje.preview);
        const ostali = p.fajlovi.filter(f => f.id !== id);
        return ostali.length ? { ...p, fajlovi: ostali } : null;
    });
    const zatvoriPriloge = () => {
        if (saljePriloge) return;
        (prilozi?.fajlovi || []).forEach(f => f.preview && URL.revokeObjectURL(f.preview));
        setPrilozi(null);
    };

    /* Kompresija slike u pregledaču: najduža strana najviše 1920 px, JPEG 82 %.
       Fotografija sa telefona (4–8 MB) tako postaje ~300–600 KB, što je i ispod
       granice od 10 MB na serveru. GIF se ne dira (izgubio bi animaciju). Providne
       PNG slike dobijaju belu pozadinu umesto crne. */
    const kompresujSliku = async (file) => {
        if (file.type === 'image/gif') return { blob: file, ime: file.name };
        const url = URL.createObjectURL(file);
        try {
            const img = await new Promise((resolve, reject) => {
                const i = new Image();
                i.onload = () => resolve(i);
                i.onerror = reject;
                i.src = url;
            });
            const MAX = 1920;
            const k = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
            const w = Math.round(img.naturalWidth * k), h = Math.round(img.naturalHeight * k);
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            const ctx = c.getContext('2d');
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            const blob = await new Promise(r => c.toBlob(r, 'image/jpeg', 0.82));
            /* Ako kompresija ne smanji fajl (mala slika već optimizovana), šalje se original. */
            if (!blob || blob.size >= file.size) return { blob: file, ime: file.name };
            return { blob, ime: file.name.replace(/\.[^.]+$/, '') + '.jpg' };
        } finally {
            URL.revokeObjectURL(url);
        }
    };

    const otpremi = async (blob, ime) => {
        const fd = new FormData();
        fd.append('file', blob, ime);
        return fetch(process.env.REACT_APP_BACKEND_URL + '/chat/uploadFile', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + auth.token },
            body: fd,
        }).then(r => { if (r.ok) return r.json(); throw r; });
    };

    const posaljiPriloge = async () => {
        const conv = activeConvRef.current;
        if (!prilozi?.fajlovi.length || !conv || !socketRef.current) return;
        setSaljePriloge(true);
        const neuspeli = [];
        for (const f of prilozi.fajlovi) {
            try {
                let blob = f.file, ime = f.file.name, tip;
                if (f.jeSlika && prilozi.rezim === 'kompresovano') {
                    ({ blob, ime } = await kompresujSliku(f.file));
                    tip = 'image';
                } else if (f.jeSlika) {
                    tip = 'file';                       // original: kao fajl, bez kompresije (Viber „pošalji kao fajl")
                } else {
                    tip = isImageFile(ime) ? 'image' : 'file';
                }
                if (blob.size > MAX_VELICINA_MB * 1024 * 1024) {
                    neuspeli.push(t('chat.prilogPrevelik', { ime: f.file.name, mb: MAX_VELICINA_MB }));
                    continue;
                }
                const res = await otpremi(blob, ime);
                socketRef.current.emit('send-message', {
                    idGrupe: conv.id, sadrzaj: null, tip,
                    filePath: res.path, fileNaziv: res.naziv,
                    replyToId: replyingTo?.id || null,
                });
            } catch {
                neuspeli.push(t('chat.prilogGreska', { ime: f.file.name }));
            }
        }
        prilozi.fajlovi.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
        setPrilozi(null);
        setReplyingTo(null);
        setSaljePriloge(false);
        if (neuspeli.length) {
            toastRef.current?.show({ severity: 'warn', summary: t('chat.prilozi'), detail: neuspeli.join('\n'), life: 6000 });
        }
    };

    // ── Edit message ──────────────────────────────────────────
    const startEdit = (msg) => {
        setEditingId(msg.id);
        setEditingText(msg.sadrzaj || '');
        setReactionPickerMsgId(null);
    };
    const saveEdit = () => {
        if (!editingText.trim() || !socketRef.current) { cancelEdit(); return; }
        socketRef.current.emit('edit-message', { idPoruke: editingId, sadrzaj: editingText.trim() });
        setEditingId(null);
        setEditingText('');
    };
    const cancelEdit = () => { setEditingId(null); setEditingText(''); };

    // ── Delete message ────────────────────────────────────────
    const deleteMessage = (idPoruke, idGrupe) => {
        socketRef.current?.emit('delete-message', { idPoruke, idGrupe });
    };

    // ── Message readers popup ────────────────────────────────────
    const openReadersPopup = async (e, msg) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setReadersPopup({ open: true, msgId: msg.id, list: [], nisu: [], loading: true, x: rect.left, y: rect.top });
        try {
            /* `saNeprocitanim` vraća i članove koji poruku JOŠ nisu pročitali —
               u grupi je to često važnije od spiska onih koji jesu. */
            const res = await apiFetch('/getMessageReaders', { idPoruke: msg.id, saNeprocitanim: true });
            setReadersPopup(p => ({ ...p, list: res?.procitali || [], nisu: res?.nisuProcitali || [], loading: false }));
        } catch {
            setReadersPopup(p => ({ ...p, loading: false }));
        }
    };
    const closeReadersPopup = () => setReadersPopup(p => ({ ...p, open: false }));

    // ── Delete DM ────────────────────────────────────────────────
    const deleteConversation = (e, conv) => {
        e.stopPropagation();
        confirmDialog({
            message: 'Obrisati ovaj razgovor?',
            acceptLabel: 'Da',
            rejectLabel: 'Ne',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await apiFetch('/deleteConversation', { idGrupe: conv.id });
                    setConversations(cs => cs.filter(c => c.id !== conv.id));
                    if (activeConv?.id === conv.id) { setActiveConv(null); setMessages([]); setImaStarijih(false); setMobileShowConv(false); }
                } catch {}
            },
        });
    };

    // ── Leave group ───────────────────────────────────────────────
    const leaveGroup = () => {
        if (!activeConv) return;
        confirmDialog({
            message: 'Napustiti ovu grupu?',
            acceptLabel: 'Da',
            rejectLabel: 'Ne',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await apiFetch('/leaveGroup', { idGrupe: activeConv.id });
                    setConversations(cs => cs.filter(c => c.id !== activeConv.id));
                    setActiveConv(null);
                    setMessages([]);
                    setImaStarijih(false);
                    setMobileShowConv(false);
                    closeGroupPanel();
                } catch {}
            },
        });
    };

    // ── GIF search ───────────────────────────────────────────────
    const openGifModal = () => {
        setShowAppLauncher(false);
        setGifSearch('');
        setGifResults([]);
        setShowGifModal(true);
        fetchGifs('');
    };
    const fetchGifs = async (q) => {
        setGifLoading(true);
        try {
            const res = await apiFetch('/searchGifs', { q });
            setGifResults(res);
        } catch {}
        setGifLoading(false);
    };
    const handleGifSearchChange = (e) => {
        const val = e.target.value;
        setGifSearch(val);
        clearTimeout(gifSearchTimer.current);
        gifSearchTimer.current = setTimeout(() => fetchGifs(val), 400);
    };
    const sendGif = (gifUrl) => {
        if (!activeConv || !socketRef.current) return;
        socketRef.current.emit('send-message', {
            idGrupe:  activeConv.id,
            sadrzaj:  gifUrl,
            tip:      'gif',
            replyToId: replyingTo?.id || null,
        });
        setReplyingTo(null);
        setShowGifModal(false);
    };

    // ── Location share (Deo T) — korisnik BIRA proizvoljnu tačku na mapi, ne šalje se
    // više samo trenutna GPS pozicija (mada mapa nudi "Moja lokacija" dugme za to).
    const openLocationPicker = () => {
        setShowAppLauncher(false);
        setShowLocationModal(true);
    };

    const confirmLocation = (lat, lng) => {
        socketRef.current?.emit('send-message', {
            idGrupe:  activeConv.id,
            sadrzaj:  `${lat},${lng}`,
            tip:      'location',
            replyToId: replyingTo?.id || null,
        });
        setReplyingTo(null);
        setShowLocationModal(false);
    };

    // ── Poll handlers ────────────────────────────────────────────
    const openPollModal = () => { setShowAppLauncher(false); setPollQuestion(''); setPollOptions(['', '']); setShowPollModal(true); };
    const setPollOption = (idx, val) => setPollOptions(prev => prev.map((o, i) => i === idx ? val : o));
    const addPollOption = () => setPollOptions(prev => prev.length < 6 ? [...prev, ''] : prev);
    const removePollOption = (idx) => setPollOptions(prev => prev.length > 2 ? prev.filter((_, i) => i !== idx) : prev);

    const submitPoll = async () => {
        const clean = pollOptions.map(o => o.trim()).filter(Boolean);
        if (!pollQuestion.trim() || clean.length < 2 || !activeConv) return;
        try {
            const res = await apiFetch('/createPoll', { idGrupe: activeConv.id, pitanje: pollQuestion.trim(), opcije: clean });
            socketRef.current?.emit('send-message', {
                idGrupe:  activeConv.id,
                sadrzaj:  String(res.pollId),
                tip:      'poll',
                replyToId: replyingTo?.id || null,
            });
            setReplyingTo(null);
            setShowPollModal(false);
        } catch {}
    };

    const votePoll = (msg, opcija_id) => {
        if (!socketRef.current) return;
        socketRef.current.emit('vote-poll', {
            idAnkete: parseInt(msg.sadrzaj),
            idOpcije: opcija_id,
            idGrupe:  msg.id_grupe,
            idPoruke: msg.id,
        });
    };

    // ── Event handlers ────────────────────────────────────────────
    const openEventModal = () => { setShowAppLauncher(false); setEventNaziv(''); setEventDatum(''); setEventOpis(''); setShowEventModal(true); };

    const submitEvent = async () => {
        if (!eventNaziv.trim() || !eventDatum || !activeConv) return;
        try {
            const res = await apiFetch('/createEvent', {
                idGrupe:    activeConv.id,
                naziv:      eventNaziv.trim(),
                datumVreme: new Date(eventDatum).toISOString(),
                opis:       eventOpis.trim() || null,
            });
            socketRef.current?.emit('send-message', {
                idGrupe:  activeConv.id,
                sadrzaj:  String(res.eventId),
                tip:      'event',
                replyToId: replyingTo?.id || null,
            });
            setReplyingTo(null);
            setShowEventModal(false);
        } catch {}
    };

    // ── Send reaction ─────────────────────────────────────────
    const sendReaction = (idPoruke, idGrupe, emoji) => {
        socketRef.current?.emit('react-message', { idPoruke, idGrupe, emoji });
        setReactionPickerMsgId(null);
    };

    // ── Meni poruke (desni klik / dugi pritisak) ─────────────────
    const zatvoriMenije = () => { setCtxMeni(null); setPunIzbor(null); };

    const otvoriMeni = (e, msg, x, y) => {
        if (msg.tip === 'system' || msg.obrisan) return;
        e.preventDefault();
        e.stopPropagation();
        /* U režimu „Izaberi više" desni klik / dugi pritisak samo bira poruku. */
        if (izborRef.current) { prebaciIzbor(msg.id); return; }
        setPunIzbor(null);
        setCtxMeni({ msg, x: x ?? e.clientX, y: y ?? e.clientY });
    };

    /* Telefon nema desni klik — dugi pritisak (500 ms) otvara isti meni. Pomeranje
       prsta ga otkazuje, da skrol kroz poruke ne bi otvarao meni. */
    const dugiPritisak = useRef(null);
    const pocniDugiPritisak = (e, msg) => {
        const t0 = e.touches?.[0];
        if (!t0) return;
        clearTimeout(dugiPritisak.current);
        const { clientX, clientY } = t0;
        dugiPritisak.current = setTimeout(() => otvoriMeni({ preventDefault() {}, stopPropagation() {} }, msg, clientX, clientY), 500);
    };
    const prekiniDugiPritisak = () => clearTimeout(dugiPritisak.current);

    /* Meni i izbor se drže unutar prozora: posle iscrtavanja se izmere i, ako
       bi izašli preko ivice, pomere se nazad. Ref-callback, jer se element
       pojavljuje i nestaje sa stanjem. */
    const drziUProzoru = (el) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const m = 8;
        if (r.right  > window.innerWidth  - m) el.style.left = `${Math.max(m, window.innerWidth  - r.width  - m)}px`;
        if (r.bottom > window.innerHeight - m) el.style.top  = `${Math.max(m, window.innerHeight - r.height - m)}px`;
    };

    const otvoriPunIzbor = (e, msg) => {
        e.stopPropagation();
        const x = ctxMeni?.x ?? e.clientX;
        const y = ctxMeni?.y ?? e.clientY;
        setCtxMeni(null);
        setPunIzbor({ msg, x, y });
    };

    /* Info o poruci — isti prozor „ko je pročitao / ko nije" koji se otvara klikom
       na brojač u grupi, samo dostupan i iz menija (i u razgovoru 1 na 1). */
    const otvoriInfo = async (msg, x, y) => {
        setReadersPopup({ open: true, msgId: msg.id, list: [], nisu: [], loading: true, x, y });
        try {
            const res = await apiFetch('/getMessageReaders', { idPoruke: msg.id, saNeprocitanim: true });
            setReadersPopup(p => ({ ...p, list: res?.procitali || [], nisu: res?.nisuProcitali || [], loading: false }));
        } catch {
            setReadersPopup(p => ({ ...p, loading: false }));
        }
    };

    // ── Prosledi ──────────────────────────────────────────────
    const mozeProsledjivanje = (m) => !['poll', 'event', 'system'].includes(m.tip) && !m.obrisan;
    /* Prima jednu poruku ili niz (Izaberi više). Neprosledive (anketa, event,
       obrisana) se izbacuju odmah, da prozor ne obećava ono što server odbija. */
    const otvoriProsledi = (msgs) => {
        const lista = (Array.isArray(msgs) ? msgs : [msgs]).filter(mozeProsledjivanje);
        if (!lista.length) return;
        setProsledi(lista); setProsledjivanjeU([]); setProsledPretraga('');
    };
    const zatvoriProsledi = () => { setProsledi(null); setProsledjivanjeU([]); setProsledPretraga(''); };
    const prebaciCilj = (id) => setProsledjivanjeU(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 20 ? prev : [...prev, id]));
    const posaljiProsledjeno = () => {
        if (!prosledi?.length || !prosledjivanjeU.length) return;
        /* Hronološki (po id), da redosled u cilju bude isti kao u izvoru. Server
           prima jednu poruku po događaju — tako ostaje jedna provera po poruci. */
        [...prosledi].sort((a, b) => a.id - b.id).forEach(m =>
            socketRef.current?.emit('forward-message', { idPoruke: m.id, ciljevi: prosledjivanjeU }));
        zatvoriProsledi();
        setIzbor(null);
    };

    // ── Sačuvaj (lična zvezdica) ──────────────────────────────
    const sacuvajPoruke = async (ids, sacuvaj) => {
        if (!ids?.length) return;
        try {
            const odg = await apiFetch('/sacuvaj', { idPoruke: ids, sacuvaj });
            const potvrdjeno = new Set(odg?.ids || []);
            setMessages(msgs => msgs.map(m => potvrdjeno.has(m.id) ? { ...m, sacuvana: sacuvaj ? 1 : 0 } : m));
            if (!sacuvaj) setSacuvane(lista => lista.filter(s => !potvrdjeno.has(s.id)));
        } catch {
            toastRef.current?.show({ severity: 'error', summary: t('chat.sacuvane'), detail: t('chat.sacuvajGreska'), life: 3000 });
        }
    };

    const otvoriSacuvane = async () => {
        setSacuvaneOtvoreno(true);
        setSacuvaneUcitava(true);
        try {
            const lista = await apiFetch('/sacuvane', {});
            setSacuvane(Array.isArray(lista) ? lista : []);
        } catch { setSacuvane([]); }
        setSacuvaneUcitava(false);
    };

    /* Klik na sačuvanu poruku: otvori njen razgovor i skoči na nju (dovlači se ako
       je starija od prve stranice — isto kao skok na pogodak pretrage). */
    const idiNaSacuvanu = async (s) => {
        const conv = conversations.find(c => c.id === s.id_grupe);
        if (!conv) return;
        setSacuvaneOtvoreno(false);
        if (activeConv?.id !== conv.id) {
            await openConversation(conv);
            await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        }
        scrollToMessage(s.id);
    };

    // ── Izaberi više ──────────────────────────────────────────
    const pocniIzbor = (msg) => { setIzbor(msg ? [msg.id] : []); setReplyingTo(null); setShowEmoji(false); };
    const prebaciIzbor = (id) => setIzbor(prev => prev ? (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]) : prev);
    const izabranePoruke = () => {
        const s = new Set(izbor || []);
        return messages.filter(m => s.has(m.id));
    };
    /* Kopiranje više poruka — kao WhatsApp: „[datum vreme] Ime: tekst", po redu. */
    const kopirajIzabrane = () => {
        const tekst = izabranePoruke()
            .filter(m => m.tip === 'text' && m.sadrzaj && !m.obrisan)
            .map(m => `[${datumVremeTekst(m.datum)}] ${m.ime_korisnika || ''}: ${m.sadrzaj}`)
            .join('\n');
        if (!tekst) return;
        kopirajPoruku({ id: -1, sadrzaj: tekst });
        toastRef.current?.show({ severity: 'success', summary: t('chat.kopirano'), life: 2000 });
        setIzbor(null);
    };
    const obrisiIzabrane = () => {
        const moje = izabranePoruke().filter(m => m.id_korisnik === myId && (m.tip_korisnika || 'OPERATER') === 'OPERATER' && !m.obrisan);
        if (!moje.length) return;
        confirmDialog({
            message: t('chat.obrisiIzabrane', { broj: moje.length }),
            acceptLabel: t('common.yes'),
            rejectLabel: t('common.no'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => { moje.forEach(m => deleteMessage(m.id, m.id_grupe)); setIzbor(null); },
        });
    };

    // ── Zakači / otkači ───────────────────────────────────────
    const zakacenSet = useMemo(() => new Set(zakacene.map(z => z.id_poruke)), [zakacene]);
    const zakaci  = (m) => socketRef.current?.emit('pin-message',   { idPoruke: m.id });
    const otkaci  = (id) => socketRef.current?.emit('unpin-message', { idPoruke: id });

    // ── Napravi zadatak iz poruke ─────────────────────────────
    const datumVremeTekst = (dt) => {
        const d = new Date(dt);
        if (Number.isNaN(d.getTime())) return '';
        const p2 = (n) => String(n).padStart(2, '0');
        return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()}. ${p2(d.getHours())}:${p2(d.getMinutes())}`;
    };

    const otvoriZadatak = (msg) => {
        /* Tekst zadatka = tekst poruke (ili opis priloga), plus odakle je došao —
           da se iz zadatka zna kome se obratiti i gde je prepiska. Sve je izmenljivo. */
        const sadrzaj = msg.tip === 'text' ? (msg.sadrzaj || '')
                      : msg.tip === 'image' ? '📷 Slika'
                      : msg.tip === 'location' ? `📍 https://www.google.com/maps?q=${msg.sadrzaj}`
                      : msg.tip === 'gif' ? (msg.sadrzaj || '')
                      : `📎 ${msg.file_naziv || 'Fajl'}`;
        const razgovor = activeConv?.naziv_prikaz || activeConv?.naziv || '';
        const izvor = `— ${t('chat.izChata')}: ${razgovor}${msg.ime_korisnika && msg.ime_korisnika !== razgovor ? `, ${msg.ime_korisnika}` : ''}, ${datumVremeTekst(msg.datum)}`;
        setZadatak({ msg, tekst: `${sadrzaj}\n\n${izvor}`, kome: [], prioritet: null });
        setZadPretraga('');

        const zaglavlje = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token };
        if (!zadKorisnici.length) {
            fetch(process.env.REACT_APP_BACKEND_URL + '/users', { method: 'POST', headers: zaglavlje })
                .then(handleErrors)
                .then(r => setZadKorisnici((r?.users || []).map(u => ({ value: u.id, label: `${u.name} ${u.surname}`, email: u.email, avatar: u.avatar }))))
                .catch(() => {});
        }
        if (!zadPrioriteti.length) {
            fetch(process.env.REACT_APP_BACKEND_URL + '/plan/prioritet_plana', { method: 'GET', headers: zaglavlje })
                .then(handleErrors)
                .then(r => setZadPrioriteti((r || []).map(p => ({ value: p.id, label: p.naziv_prioriteta }))))
                .catch(() => {});
        }
    };

    const zatvoriZadatak = () => { if (!zadSalje) setZadatak(null); };

    /* Slika iz poruke ide u zadatak kao prilog (wall.Plan_Slike čuva data-URL,
       isti oblik koji tabla zadataka šalje pri lepljenju slike). */
    const slikaUDataUrl = async (putanja) => {
        const blob = await fetch(fileUrl(putanja)).then(r => { if (!r.ok) throw r; return r.blob(); });
        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
        });
    };

    const posaljiZadatak = async () => {
        if (!zadatak) return;
        const tekst = zadatak.tekst.trim();
        if (tekst.length < 2 || !zadatak.kome.length || !zadatak.prioritet) {
            toastRef.current?.show({ severity: 'warn', summary: t('chat.napraviZadatak'), detail: t('chat.zadatakPopuni'), life: 3000 });
            return;
        }
        setZadSalje(true);
        try {
            const odg = await fetch(process.env.REACT_APP_BACKEND_URL + '/plan/createPlan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                body: JSON.stringify({ text: tekst, taskZa: zadatak.kome, prioritet_id: zadatak.prioritet }),
            }).then(handleErrors);

            const m = zadatak.msg;
            if (odg?.id && m.tip === 'image' && m.file_path) {
                /* Prilog je dodatak: ako slika ne može da se pročita, zadatak ostaje
                   napravljen, a korisnik dobija upozorenje umesto tihog gubitka. */
                try {
                    const slika = await slikaUDataUrl(m.file_path);
                    await fetch(process.env.REACT_APP_BACKEND_URL + '/plan/slike/add', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                        body: JSON.stringify({ plan_id: odg.id, komentar_id: null, slika, filename: m.file_naziv || 'chat.png' }),
                    }).then(handleErrors);
                } catch {
                    toastRef.current?.show({ severity: 'warn', summary: t('chat.napraviZadatak'), detail: t('chat.zadatakBezSlike'), life: 4000 });
                }
            }

            toastRef.current?.show({ severity: 'success', summary: t('chat.napraviZadatak'), detail: t('chat.zadatakNapravljen', { id: odg?.id ?? '' }), life: 3000 });
            setZadatak(null);
        } catch (err) {
            let poruka = '';
            try { poruka = (await err.json?.())?.message || ''; } catch {}
            toastRef.current?.show({ severity: 'error', summary: t('chat.napraviZadatak'), detail: poruka || t('chat.zadatakGreska'), life: 4000 });
        }
        setZadSalje(false);
    };

    useEffect(() => {
        if (!izbor) return undefined;
        const naEsc = (e) => { if (e.key === 'Escape') setIzbor(null); };
        window.addEventListener('keydown', naEsc);
        return () => window.removeEventListener('keydown', naEsc);
    }, [izbor]);

    useEffect(() => {
        if (!ctxMeni && !punIzbor) return undefined;
        const naEsc = (e) => { if (e.key === 'Escape') zatvoriMenije(); };
        window.addEventListener('keydown', naEsc);
        return () => window.removeEventListener('keydown', naEsc);
    }, [ctxMeni, punIzbor]);

    // ── Emoji insert ──────────────────────────────────────────
    const insertEmoji = (emoji) => {
        if (!inputRef.current) return;
        inputRef.current.focus();
        const sel = window.getSelection();
        if (sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(emoji.native));
            range.collapse(false);
        } else {
            inputRef.current.innerText += emoji.native;
        }
    };

    // ── File upload ───────────────────────────────────────────
    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file || !activeConv) return;
        setUploadingFile(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/chat/uploadFile', {
                method:  'POST',
                headers: { Authorization: 'Bearer ' + auth.token },
                body:    fd,
            }).then(r => { if (r.ok) return r.json(); throw r; });

            const tip = isImageFile(res.path) ? 'image' : 'file';
            socketRef.current?.emit('send-message', {
                idGrupe:   activeConv.id,
                sadrzaj:   null,
                tip,
                filePath:  res.path,
                fileNaziv: res.naziv,
            });
        } catch {}
        setUploadingFile(false);
        fileInputRef.current.value = '';
    };

    // ── New chat modal ────────────────────────────────────────
    const openNewChatModal = async () => {
        setShowNewModal(true);
        setModalTab('dm');
        setModalSearch('');
        setModalCategory('OPERATER');
        setSelectedUsers([]);
        setGroupName('');
        /* Uvek sveže — spisak nosi i „poslednji put aktivan", koji bi iz keša bio
           star. Dok novi ne stigne, ostaje prikazan stari (bez praznog trzaja). */
        try {
            const users = await apiFetch('/getCompanyUsers', {});
            setCompanyUsers(users);
        } catch {}
    };

    // ── Settings / avatar / theme ─────────────────────────────
    const openSettings  = () => setShowSettings(true);
    const closeSettings = () => setShowSettings(false);
    /* Prekidač teme u chatu radi ISTO što i Administracija → Korisnik → Tema:
       primeni na celu aplikaciju, javi Redux-u i snimi na nalog. Ranije je menjao
       samo lokalno stanje chata, pa se izbor gubio čim se chat otkači — a upisivao
       se u localStorage ključ `chatTheme` koji niko nikad nije čitao. */
    /* Postavlja ZADATU temu. Kartice biraju temu imenom (ima ih tri), a
       prekidač dan/noć ispod njih i dalje samo prebacuje svetlo/tamno. */
    const postaviTemu = (nova, e) => {
        setTheme(nova);
        /* Isti prelaz kao u Administracija → Korisnik: nova tema se otkriva krugom
           iz tačke klika. Bez ovoga bi ista radnja izgledala različito zavisno
           od toga odakle je pokrenuta. */
        const okvir = e?.currentTarget?.getBoundingClientRect?.();
        const izvor = okvir ? { x: okvir.left + okvir.width / 2, y: okvir.top + okvir.height / 2 } : null;
        applyThemeWithReveal(nova, izvor);             // <html data-theme> + localStorage.appTheme
        dispatch({ type: 'THEME_SET', value: nova });  // ostatak aplikacije odmah prati

        // Snimanje na nalog je jedino što može da otkaže; izgled se ne vraća zbog toga.
        fetch(process.env.REACT_APP_BACKEND_URL + '/users/theme', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ tema: nova }),
        }).catch(() => {});
    };

    /* Prekidač dan/noć: iz retroa izlazi na svetlu, inače prebacuje. */
    /* Prekidac menja SAMO osu svetlo/tamno i ne izlazi iz retroa — isto kao u
       licnim podesavanjima. Kartice iznad biraju temu imenom. */
    const toggleTheme = (e) => postaviTemu(
        theme === 'retro' ? 'retrodark' : theme === 'retrodark' ? 'retro' : theme === 'dark' ? 'light' : 'dark', e);

    const handleAvatarSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAvatarUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/chat/uploadAvatar', {
                method:  'POST',
                headers: { Authorization: 'Bearer ' + auth.token },
                body:    fd,
            }).then(r => { if (r.ok) return r.json(); throw r; });
            setMyAvatar(res.avatar);
            /* Zaglavlje prati promenu odmah, bez ponovne prijave. */
            dispatch({ type: 'AVATAR_SET', value: res.avatar || null });
            loadConversations();               // osveži avatare u listi razgovora
            if (companyUsers.length) setCompanyUsers([]);  // primoraj re-fetch sa novim avatarom
        } catch {}
        setAvatarUploading(false);
        if (avatarInputRef.current) avatarInputRef.current.value = '';
    };

    // ── Slika grupe (admin) ────────────────────────────────────
    const handleGroupAvatarSelect = async (e) => {
        const file = e.target.files[0];
        if (!file || !activeConv) return;
        setGroupAvatarUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('idGrupe', activeConv.id);
        try {
            const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/chat/uploadGroupAvatar', {
                method:  'POST',
                headers: { Authorization: 'Bearer ' + auth.token },
                body:    fd,
            }).then(r => { if (r.ok) return r.json(); throw r; });
            setGroupInfo(gi => gi ? { ...gi, avatar: res.avatar } : gi);
            loadConversations();
        } catch {}
        setGroupAvatarUploading(false);
        if (groupAvatarInputRef.current) groupAvatarInputRef.current.value = '';
    };

    // ── Preimenovanje grupe (admin) ────────────────────────────
    const saveGroupName = async () => {
        const naziv = groupNameInput.trim();
        if (!naziv || !activeConv) { setGroupNameEditing(false); return; }
        try {
            const res = await apiFetch('/renameGroup', { idGrupe: activeConv.id, naziv });
            setGroupInfo(gi => gi ? { ...gi, naziv: res.naziv } : gi);
            loadConversations();
        } catch {}
        setGroupNameEditing(false);
    };

    const toggleSelectUser = (uid, tip) => {
        const key = ukey(tip, uid);
        setSelectedUsers(prev =>
            prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]
        );
    };

    const createChat = async () => {
        if (!selectedUsers.length) return;
        try {
            if (modalTab === 'dm') {
                const [targetTip, targetUserIdRaw] = selectedUsers[0].split(':');
                const targetUserId = Number(targetUserIdRaw);
                const res = await apiFetch('/createDirectChat', { targetUserId, targetTip });
                socketRef.current?.emit('join-room', { idGrupe: res.id });
                loadConversations();
                const targetUser = companyUsers.find(u => ukey(u.tip, u.value) === selectedUsers[0]);
                const fakeConv = { id: res.id, tip: 1, naziv_prikaz: targetUser?.label || 'DM', neprocitano: 0 };
                setShowNewModal(false);
                openConversation(fakeConv);
            } else {
                if (!groupName.trim()) return;
                const members = selectedUsers.map(k => { const [tip, id] = k.split(':'); return { id: Number(id), tip }; });
                const res = await apiFetch('/createGroup', { naziv: groupName.trim(), members });
                socketRef.current?.emit('join-room', { idGrupe: res.id });
                loadConversations();
                setShowNewModal(false);
            }
        } catch {}
    };

    // ── Typing label for active conv ──────────────────────────
    const typingLabel = activeConv
        ? Object.entries(typingUsers)
            .filter(([k]) => k.startsWith(`${activeConv.id}_`))
            .map(([, name]) => name)
            .join(', ')
        : '';

    // ── Filtered conversations ────────────────────────────────
    const filteredConvs = conversations.filter(c =>
        (c.naziv_prikaz || '').toLowerCase().includes(convSearch.toLowerCase())
    );

    // ── Filtered modal users ──────────────────────────────────
    /* Koliko ih ima u svakoj kategoriji — brojac na prekidacu odmah kaze
       ima li smisla prebaciti se, bez klika u prazno. */
    const brojPoTipu = (tip) => companyUsers.filter(u => (u.tip || 'OPERATER') === tip).length;

    const filteredUsers = companyUsers
        .filter(u => (u.tip || 'OPERATER') === modalCategory)
        .filter(u =>
            u.label.toLowerCase().includes(modalSearch.toLowerCase()) ||
            (u.email || '').toLowerCase().includes(modalSearch.toLowerCase())
        );

    // ── Group panel derived ───────────────────────────────────
    const isGroupAdmin = groupInfo?.clanovi?.some(c => c.id_user === myId && (c.tip || 'OPERATER') === 'OPERATER' && c.uloga === 2);
    const currentMemberKeys = new Set((groupInfo?.clanovi || []).map(c => ukey(c.tip, c.id_user)));

    /* Stanje sagovornika u zaglavlju razgovora — tri stanja, ne dva.

         zeleno  na mrezi i nesto radi
         zuto    na mrezi, ali bez ijedne radnje duze od 15 minuta
         sivo    nije na mrezi

       U zuta i siva varijanta ide i KADA je poslednji put bio aktivan. Za zute to
       zna socket; za sive stize uz spisak razgovora (`dm_poslednja_aktivnost`), jer
       o odjavljenom korisniku server u memoriji nema nista.

       Grupe nemaju prisustvo — tamo i dalje stoji broj clanova. */
    const stanjeSagovornika = useMemo(() => {
        if (!activeConv || activeConv.tip !== 1 || !activeConv.dm_user_id) return null;
        const kljuc = ukey(activeConv.dm_user_tip, activeConv.dm_user_id);
        const p = prisustvo?.[kljuc];
        if (p) return { stanje: p.stanje || 'ONLINE', kada: p.poslednjaAktivnost };
        /* Nema ga u socket stanju => offline. Rezervno: stariji server jos ne salje
           `presence:stanje`, pa se pada na stari spisak i prikaz ostaje kao pre. */
        if (onlineUsers.includes(kljuc)) return { stanje: 'ONLINE', kada: null };
        return { stanje: 'OFFLINE', kada: activeConv.dm_poslednja_aktivnost || null };
    }, [activeConv, prisustvo, onlineUsers]);

    /* „danas u 15:30" je citljivije od punog datuma za ono sto se desilo danas —
       a za starije se pun datum i trazi. */
    const kadaTekst = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        const danas = new Date();
        const istiDan = d.toDateString() === danas.toDateString();
        const sat = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        if (istiDan) return `${t('chat.danas')} ${sat}`;
        const juce = new Date(danas); juce.setDate(juce.getDate() - 1);
        if (d.toDateString() === juce.toDateString()) return `${t('chat.juce')} ${sat}`;
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}. ${sat}`;
    };

    /* Prisutnost: `onlineUsers` sa socketa je spisak ključeva u istom obliku kao
       `ukey` — `OPERATER:12` / `MOBILNI:5`. Kroz Set, jer se spisak članova
       prolazi u petlji pri svakom renderu panela. */
    const onlineSet = useMemo(() => new Set(onlineUsers), [onlineUsers]);
    const jeNaMrezi  = (c) => onlineSet.has(ukey(c.tip, c.id_user));

    /* Stanje BILO KOG korisnika — isto pravilo kao `stanjeSagovornika` u zaglavlju,
       samo za spisak (+) i članove grupe. Povezani: stanje iz socketa (ono je
       sveže). Ostali: OFFLINE, a „kada" iz baze (`poslednja_aktivnost`). */
    const stanjeKorisnika = (tip, id, izBaze) => {
        const k = ukey(tip, id);
        const p = prisustvo?.[k];
        if (p) return { stanje: p.stanje || 'ONLINE', kada: p.poslednjaAktivnost };
        if (onlineSet.has(k)) return { stanje: 'ONLINE', kada: null };
        return { stanje: 'OFFLINE', kada: izBaze || null };
    };

    /* Tačka na avataru — ISTO pravilo kao natpis u zaglavlju razgovora. Ranije je
       tačka gledala samo „ima li živ socket", pa je ista osoba u listi bila zelena,
       a u zaglavlju „odsutan" (otvorena kartica, a čovek ne radi). */
    const tackaZa = (tip, id) => {
        if (id == null) return null;
        const st = stanjeKorisnika(tip, id).stanje;
        return st === 'ONLINE' ? 'ONLINE' : st === 'AWAY' ? 'AWAY' : null;
    };

    /* Ista oznaka (tačka + tekst) svuda — u zaglavlju, u spisku za nov razgovor i
       u članovima grupe. Za nekoga ko se nikad nije prijavio nema „kada", pa se
       tada (osim u zaglavlju) ne piše ništa umesto golog „offline". */
    const oznakaPrisustva = (s, { kompaktno = false } = {}) => {
        if (!s) return null;
        if (kompaktno && s.stanje === 'OFFLINE' && !s.kada) return null;
        return (
            <span className={`chat-prisustvo chat-prisustvo-${s.stanje.toLowerCase()}${kompaktno ? ' kompaktno' : ''}`}>
                <span className="chat-prisustvo-tacka" />
                {s.stanje === 'ONLINE' && t('chat.online')}
                {s.stanje === 'AWAY' && (
                    s.kada
                        ? `${t('chat.odsutan')} · ${t('chat.poslednjiPutAktivan')} ${kadaTekst(s.kada)}`
                        : t('chat.odsutan')
                )}
                {s.stanje === 'OFFLINE' && (
                    s.kada
                        ? `${t('chat.poslednjiPutAktivan')} ${kadaTekst(s.kada)}`
                        : t('chat.offline')
                )}
            </span>
        );
    };
    const brojNaMrezi = (clanovi) => (clanovi || []).filter(jeNaMrezi).length;
    const addableUsers = companyUsers
        .filter(u => (u.tip || 'OPERATER') === addMemberCategory)
        .filter(u => !currentMemberKeys.has(ukey(u.tip, u.value)))
        .filter(u => !addMemberSearch || u.label.toLowerCase().includes(addMemberSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(addMemberSearch.toLowerCase()));

    // ── Slike u trenutnom razgovoru (za navigaciju u preview-u) ──
    const chatImages = messages
        .filter(m => (m.tip === 'image' && m.file_path) || (m.tip === 'gif' && m.sadrzaj))
        .map(m => m.tip === 'image' ? fileUrl(m.file_path) : m.sadrzaj);

    const openImgPreview = (url) => {
        const idx = chatImages.indexOf(url);
        setPreviewIndex(idx === -1 ? null : idx);
        setImgPreview(url);
    };

    const closeImgPreview = () => {
        setImgPreview(null);
        setPreviewIndex(null);
    };

    const showPrevImg = () => {
        if (previewIndex === null || previewIndex <= 0) return;
        const idx = previewIndex - 1;
        setPreviewIndex(idx);
        setImgPreview(chatImages[idx]);
    };

    const showNextImg = () => {
        if (previewIndex === null || previewIndex >= chatImages.length - 1) return;
        const idx = previewIndex + 1;
        setPreviewIndex(idx);
        setImgPreview(chatImages[idx]);
    };

    /* ── UVEĆANJE OTVORENE SLIKE ───────────────────────────────────────────
       Točkić uvećava sliku, a NE stranu. `Ctrl + točkić` je pregledačeva
       prečica za uvećanje cele strane, pa se oba slučaja presreću i gase sa
       `preventDefault()`.

       Slušalac je NATIVAN, sa `{ passive: false }` — Reactov `onWheel` je
       pasivan, a u pasivnom slušaocu `preventDefault()` nema dejstva (pregledač
       ga ignoriše i jos ispise upozorenje). Bez toga bi se uz sliku uvećavao i
       ceo TMS ispod nje. */
    useEffect(() => {
        const sloj = slojSlikeRef.current;
        if (!imgPreview || !sloj) return;
        const naTockic = (e) => {
            e.preventDefault();
            setZum(prethodni => {
                const sledeci = prethodni * (e.deltaY < 0 ? 1.15 : 1 / 1.15);
                const ogranicen = Math.min(8, Math.max(1, sledeci));
                /* Na povratku u prirodnu veličinu se briše i pomeranje — inače bi
                   slika ostala odgurnuta van vidnog polja. */
                if (ogranicen === 1) setPomak({ x: 0, y: 0 });
                return ogranicen;
            });
        };
        sloj.addEventListener('wheel', naTockic, { passive: false });
        return () => sloj.removeEventListener('wheel', naTockic);
    }, [imgPreview]);

    /* Promena slike i zatvaranje vraćaju uvećanje na početak. */
    useEffect(() => { setZum(1); setPomak({ x: 0, y: 0 }); }, [imgPreview]);

    const naPritisakSlike = (e) => {
        if (zum <= 1) return;
        e.preventDefault();
        prevlacenjeOd.current = { x: e.clientX - pomak.x, y: e.clientY - pomak.y };
    };
    const naPomerajSlike = (e) => {
        if (!prevlacenjeOd.current) return;
        setPomak({ x: e.clientX - prevlacenjeOd.current.x, y: e.clientY - prevlacenjeOd.current.y });
    };
    const naOtpustSlike = () => { prevlacenjeOd.current = null; };

    /* ── SKIDANJE ──────────────────────────────────────────────────────────
       Ide preko `fetch` pa `blob`, a ne kao <a download> na izvornu adresu:
       atribut `download` pregledač poštuje samo za isto poreklo, pa bi se slika
       sa drugog porekla samo otvorila u novoj kartici umesto da se sačuva. */
    const skiniSliku = async () => {
        try {
            const odgovor = await fetch(imgPreview);
            const blob = await odgovor.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = decodeURIComponent(imgPreview.split('?')[0].split('/').pop() || 'slika.jpg');
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toastRef.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('chat.skidanjeNijeUspelo'), life: 4000 });
        }
    };

    /* ── KOPIRANJE U OSTAVU (Ctrl + C, kao na Viberu) ──────────────────────
       Pregledači u ostavu primaju sliku SAMO kao PNG, pa se sve što nije PNG
       prvo propusti kroz canvas. Bez toga `ClipboardItem` odbija JPEG, a to je
       većina slika iz chata.

       Traži bezbedan kontekst (https ili localhost) — na produkciji je https,
       pa radi; ako ne uspe, javlja se porukom umesto da tiho ne uradi ništa. */
    const uPng = (blob) => new Promise((uspeh, neuspeh) => {
        const slika = new Image();
        slika.onload = () => {
            const platno = document.createElement('canvas');
            platno.width = slika.naturalWidth;
            platno.height = slika.naturalHeight;
            platno.getContext('2d').drawImage(slika, 0, 0);
            platno.toBlob(b => (b ? uspeh(b) : neuspeh(new Error('canvas'))), 'image/png');
        };
        slika.onerror = neuspeh;
        slika.src = URL.createObjectURL(blob);
    });

    const kopirajSliku = async () => {
        try {
            const odgovor = await fetch(imgPreview);
            let blob = await odgovor.blob();
            if (blob.type !== 'image/png') blob = await uPng(blob);
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
            toastRef.current?.show({ severity: 'success', summary: t('chat.kopirano'), life: 2000 });
        } catch {
            toastRef.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('chat.kopiranjeNijeUspelo'), life: 4000 });
        }
    };

    useEffect(() => {
        if (!imgPreview) return;
        const onKeyDown = (e) => {
            if (e.key === 'ArrowLeft') showPrevImg();
            else if (e.key === 'ArrowRight') showNextImg();
            else if (e.key === 'Escape') closeImgPreview();
            /* Ctrl + C kopira sliku, kao na Viberu. `preventDefault` je tu da
               pregledač ne kopira praznu selekciju preko nje. */
            else if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
                e.preventDefault();
                kopirajSliku();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imgPreview, previewIndex, chatImages.length]);

    // ── Render ────────────────────────────────────────────────
    return (
        <>
        <ConfirmDialog />
        <Toast ref={toastRef} position="top-right" />
        <div className={`chat-root${jeTamna(theme) ? ' dark' : ''}${jeRetro(theme) ? ' retro' : ''}`} onClick={() => { if (showEmoji) setShowEmoji(false); setReactionPickerMsgId(null); zatvoriMenije(); if (readersPopup.open) closeReadersPopup(); if (showAppLauncher) setShowAppLauncher(false); }}
            onDragEnter={naPrevlacenjeUlaz}
            onDragOver={naPrevlacenjeIznad}
            onDragLeave={naPrevlacenjeIzlaz}
            onDrop={(e) => naPustanje(e)}
        >

            {/* ── LEFT: conversation list ── */}
            <div className={`chat-left${mobileShowConv ? ' hidden' : ''}`}>
                <div className="chat-left-header">
                    <h2>{t('nav.chat')}</h2>
                    <div className="chat-left-header-actions">
                        <button className="chat-new-btn" onClick={otvoriSacuvane} title={t('chat.sacuvane')}>
                            <i className="pi pi-star" />
                        </button>
                        <button className="chat-new-btn" onClick={() => setSnimciOtvoreno(true)} title={t('snimci.tab')}>
                            <i className="pi pi-microphone" />
                        </button>
                        <button className="chat-new-btn" onClick={openSettings} title={t('chat.settings')}>
                            <i className="pi pi-cog" />
                        </button>
                        <button className="chat-new-btn" onClick={openNewChatModal} title={t('chat.newConversation')}>
                            <i className="pi pi-plus" />
                        </button>
                    </div>
                </div>
                <div className="chat-search-wrap">
                    <input
                        placeholder={t('chat.searchConvPh')}
                        value={convSearch}
                        onChange={e => setConvSearch(e.target.value)}
                    />
                </div>
                <div className="chat-conv-list">
                    {filteredConvs.map(conv => {
                        const name   = conv.naziv_prikaz || conv.naziv || 'Razgovor';
                        const jeMobilni = conv.imaMobilnogClana === 1 || conv.imaMobilnogClana === true || conv.dm_user_tip === 'MOBILNI';
                        const hasUnread = conv.neprocitano > 0;
                        const preview = conv.zadnja_tip === 'image' ? '📷 Slika'
                                      : conv.zadnja_tip === 'gif'   ? '🎬 GIF'
                                      : conv.zadnja_tip === 'audio' ? '🎤 Glasovna poruka'
                                      : conv.zadnja_tip === 'video' ? '🎥 Video'
                                      : conv.zadnja_tip === 'location' ? '📍 Lokacija'
                                      : conv.zadnja_tip === 'file'  ? '📎 Fajl'
                                      : conv.zadnja_tip === 'poll'  ? '📊 Anketa'
                                      : conv.zadnja_tip === 'event' ? '📅 Event'
                                      : (conv.zadnja_poruka || '');
                        const isHovered = hoveredConvId === conv.id;
                        return (
                            <div
                                key={conv.id}
                                className={`chat-conv-item${activeConv?.id === conv.id ? ' active' : ''}`}
                                onClick={() => openConversation(conv)}
                                onDragOver={(e) => prevucenNadRazgovor(e, conv)}
                                onDragLeave={() => {
                                    if (otvoriPrevucen.current?.convId === conv.id) {
                                        clearTimeout(otvoriPrevucen.current);
                                        otvoriPrevucen.current = null;
                                    }
                                }}
                                onMouseEnter={() => setHoveredConvId(conv.id)}
                                onMouseLeave={() => setHoveredConvId(null)}
                                style={{ position: 'relative' }}
                            >
                                <ChatAvatar
                                    name={name}
                                    avatar={conv.tip === 2 ? conv.grupa_avatar : conv.dm_avatar}
                                    online={tackaZa(conv.dm_user_tip, conv.dm_user_id)}
                                    isGroup={conv.tip === 2}
                                    onExpand={openImgPreview}
                                />
                                <div className="chat-conv-info">
                                    <div className="chat-conv-top">
                                        <span className="chat-conv-name">
                                            {name}
                                            {jeMobilni && (
                                                <i className="pi pi-mobile chat-mobile-badge" title={t('chat.mobilniUcesnik')} />
                                            )}
                                        </span>
                                        <span className={`chat-conv-time${hasUnread ? ' unread' : ''}`}>
                                            {formatTime(conv.zadnja_datum)}
                                        </span>
                                    </div>
                                    <div className="chat-conv-bottom">
                                        <span className="chat-conv-preview">{preview}</span>
                                        {hasUnread && <span className="chat-unread-badge">{conv.neprocitano}</span>}
                                    </div>
                                </div>
                                {isHovered && (
                                    <div className="chat-conv-hover-actions">
                                        {!hasUnread && conv.zadnja_datum && (
                                            <button
                                                className="chat-mark-unread-btn"
                                                onClick={(e) => markConvAsUnread(e, conv)}
                                                title={t('chat.markUnread')}
                                            >
                                                <i className="pi pi-envelope" />
                                            </button>
                                        )}
                                        {conv.tip === 1 && (
                                            <button
                                                className="chat-conv-delete-btn"
                                                onClick={(e) => deleteConversation(e, conv)}
                                                title={t('chat.deleteConv')}
                                            >
                                                <i className="pi pi-times" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── RIGHT: message area ── */}
            <div className={`chat-right${mobileShowConv ? ' mobile-active' : ''}`}>
                {/* Zone za puštanje prevučenog fajla — kao Viber: za slike dve zone
                    (slika kompresovano / fajl original), za ostalo jedna. */}
                {prevuci && (
                    <div className="chat-prevuci">
                        {!activeConv ? (
                            <div className="chat-prevuci-zona chat-prevuci-bez">
                                <i className="pi pi-arrow-left" />
                                <span className="chat-prevuci-naslov">{t('chat.prevuciNaRazgovor')}</span>
                            </div>
                        ) : prevuci.slike ? (
                            <>
                                <div className={`chat-prevuci-zona${prevuci.zona === 'slika' ? ' aktivna' : ''}`} onDragOver={(e) => nadZonom(e, 'slika')} onDragLeave={() => nadZonom(null, null)} onDrop={(e) => naPustanje(e, 'kompresovano')}>
                                    <i className="pi pi-image" />
                                    <span className="chat-prevuci-naslov">{t('chat.prevuciKaoSliku')}</span>
                                    <span className="chat-prevuci-opis">{t('chat.prevuciKaoSlikuOpis')}</span>
                                </div>
                                <div className={`chat-prevuci-zona${prevuci.zona === 'fajl' ? ' aktivna' : ''}`} onDragOver={(e) => nadZonom(e, 'fajl')} onDragLeave={() => nadZonom(null, null)} onDrop={(e) => naPustanje(e, 'original')}>
                                    <i className="pi pi-file" />
                                    <span className="chat-prevuci-naslov">{t('chat.prevuciKaoFajl')}</span>
                                    <span className="chat-prevuci-opis">{t('chat.prevuciKaoFajlOpis')}</span>
                                </div>
                            </>
                        ) : (
                            <div className={`chat-prevuci-zona${prevuci.zona === 'jedna' ? ' aktivna' : ''}`} onDragOver={(e) => nadZonom(e, 'jedna')} onDragLeave={() => nadZonom(null, null)} onDrop={(e) => naPustanje(e, 'original')}>
                                <i className="pi pi-paperclip" />
                                <span className="chat-prevuci-naslov">{t('chat.prevuciPrilozi')}</span>
                                <span className="chat-prevuci-opis">{activeConv.naziv_prikaz || activeConv.naziv || ''}</span>
                            </div>
                        )}
                    </div>
                )}
                {!activeConv ? (
                    <div className="chat-empty">
                        <i className="pi pi-comments" />
                        <p>{t('chat.selectConv')}</p>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="chat-header">
                            <button
                                className="chat-back-btn"
                                onClick={() => setMobileShowConv(false)}
                                title={t('common.back')}
                            >
                                <i className="pi pi-arrow-left" />
                            </button>
                            <ChatAvatar
                                name={activeConv.naziv_prikaz || activeConv.naziv || ''}
                                avatar={activeConv.tip === 2 ? activeConv.grupa_avatar : activeConv.dm_avatar}
                                isGroup={activeConv.tip === 2}
                                onExpand={openImgPreview}
                            />
                            <div className="chat-header-info">
                                {/* Ime se na uskom ekranu skraćuje sa tri tačke (Chat.css),
                                    pa puno ide u `title` — na računaru se vidi na prelaz mišem. */}
                                <div className="chat-header-name" title={activeConv.naziv_prikaz || activeConv.naziv || 'Razgovor'}>
                                    {activeConv.naziv_prikaz || activeConv.naziv || 'Razgovor'}
                                </div>
                                <div className="chat-header-sub">
                                    {activeConv.tip === 2
                                        ? <span className="chat-members-link" onClick={openGroupPanel}>
                                            {activeConv.broj_clanova || ''} {t('chat.membersSuffix')}
                                          </span>
                                        : oznakaPrisustva(stanjeSagovornika)}
                                </div>
                            </div>
                            {activeConv.tip === 1 && (
                                <>
                                    <button
                                        className="chat-header-search-btn"
                                        onClick={() => startCall?.(
                                            { id: activeConv.dm_user_id, tip: activeConv.dm_user_tip, name: activeConv.naziv_prikaz || activeConv.naziv },
                                            activeConv.id, 'audio'
                                        )}
                                        title={t('chat.callAudio')}
                                    >
                                        <i className="pi pi-phone" />
                                    </button>
                                    <button
                                        className="chat-header-search-btn"
                                        onClick={() => startCall?.(
                                            { id: activeConv.dm_user_id, tip: activeConv.dm_user_tip, name: activeConv.naziv_prikaz || activeConv.naziv },
                                            activeConv.id, 'video'
                                        )}
                                        title={t('chat.callVideo')}
                                    >
                                        <i className="pi pi-video" />
                                    </button>
                                </>
                            )}
                            <button
                                className={`chat-header-search-btn${showSearch ? ' active' : ''}`}
                                onClick={() => (showSearch ? closeSearch() : openSearch())}
                                title={t('chat.searchMessages')}
                            >
                                <i className="pi pi-search" />
                            </button>
                            <button
                                className={`chat-header-search-btn${showInfoPanel ? ' active' : ''}`}
                                onClick={() => (showInfoPanel ? closeInfoPanel() : openInfoPanel())}
                                title={t('chat.info')}
                            >
                                <i className="pi pi-info-circle" />
                            </button>
                        </div>

                        {/* Zakačene poruke — klik vodi na poruku (dovlači je ako je stara)
                            i prelazi na sledeću zakačenu, kao WhatsApp. */}
                        {zakacene.length > 0 && (() => {
                            const i = Math.min(zakIndex, zakacene.length - 1);
                            const z = zakacene[i];
                            return (
                                <div
                                    className="chat-pin-bar"
                                    onClick={() => { scrollToMessage(z.id_poruke); setZakIndex((i + 1) % zakacene.length); }}
                                    title={t('chat.zakacenaPoruka')}
                                >
                                    <i className="pi pi-thumbtack chat-pin-ikona" />
                                    <div className="chat-pin-tekst">
                                        <div className="chat-pin-naslov">
                                            {t('chat.zakacenaPoruka')}{zakacene.length > 1 && ` ${i + 1}/${zakacene.length}`}
                                        </div>
                                        <div className="chat-pin-pregled">
                                            <strong>{z.ime}:</strong> {z.pregled}
                                        </div>
                                    </div>
                                    <button
                                        className="chat-pin-otkaci"
                                        onClick={(e) => { e.stopPropagation(); otkaci(z.id_poruke); }}
                                        title={t('chat.otkaci')}
                                    >
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            );
                        })()}

                        {/* Traka pretrage unutar razgovora */}
                        {showSearch && (
                            <div className="chat-search-bar">
                                <div className="chat-search-bar-input">
                                    <i className="pi pi-search" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder={t('chat.searchInConvPh')}
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') { e.preventDefault(); gotoMatch(e.shiftKey ? 'prev' : 'next'); }
                                            if (e.key === 'Escape') closeSearch();
                                        }}
                                    />
                                </div>
                                <div className="chat-search-bar-nav">
                                    <span className="chat-search-count">
                                        {searchQuery.trim() ? `${currentMatch}/${searchMatches.length}` : '0/0'}
                                    </span>
                                    {/* Pretraga gleda celu istoriju; jedino ograničenje je
                                        granica broja pogodaka na serveru. */}
                                    {pretragaOdsecena && (
                                        <span className="chat-search-napomena" title={t('chat.pretragaOdsecena')}>
                                            <i className="pi pi-info-circle" />
                                        </span>
                                    )}
                                    <button onClick={() => gotoMatch('first')} disabled={!searchMatches.length} title={t('chat.first')}>
                                        <i className="pi pi-angle-double-left" />
                                    </button>
                                    <button onClick={() => gotoMatch('prev')} disabled={!searchMatches.length} title={t('chat.prev')}>
                                        <i className="pi pi-angle-left" />
                                    </button>
                                    <button onClick={() => gotoMatch('next')} disabled={!searchMatches.length} title={t('chat.next')}>
                                        <i className="pi pi-angle-right" />
                                    </button>
                                    <button onClick={() => gotoMatch('last')} disabled={!searchMatches.length} title={t('chat.last')}>
                                        <i className="pi pi-angle-double-right" />
                                    </button>
                                    <button className="chat-search-close" onClick={closeSearch} title={t('common.close')}>
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Messages */}
                        <div
                            className="chat-messages"
                            ref={messagesScrollRef}
                            onScroll={handleMessagesScroll}
                            onWheel={handleMessagesWheel}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                        >
                            {/* Starije poruke se dovlače same: skrol/točkić naviše na vrhu,
                                povlačenje prstom nadole na telefonu, ili automatski dok
                                prva stranica ne popuni ekran. Ovde samo indikator. */}
                            {imaStarijih && ucitavamStarije && (
                                <div className="chat-starije">
                                    <span className="chat-starije-tekst">
                                        <i className="pi pi-spin pi-spinner" /> {t('chat.ucitavamStarije')}
                                    </span>
                                </div>
                            )}
                            {!imaStarijih && messages.length > 0 && (
                                <div className="chat-starije">
                                    <span className="chat-starije-tekst">{t('chat.pocetakRazgovora')}</span>
                                </div>
                            )}
                            {messages.map((msg, idx) => {
                                const isMine     = msg.id_korisnik === myId && (msg.tip_korisnika || 'OPERATER') === 'OPERATER';
                                const sameSenderAsPrev = idx > 0 &&
                                    messages[idx - 1].id_korisnik === msg.id_korisnik &&
                                    (messages[idx - 1].tip_korisnika || 'OPERATER') === (msg.tip_korisnika || 'OPERATER');
                                const showDate   = idx === 0 || !sameDay(messages[idx - 1].datum, msg.datum);
                                const showName   = !isMine && activeConv.tip === 2 && (idx === 0 || !sameSenderAsPrev);
                                // Avatar (leve/recv poruke) — SAMO u grupama, na PRVOJ poruci niza istog pošiljaoca
                                const showAvatar = !isMine && activeConv.tip === 2 && (idx === 0 || !sameSenderAsPrev);
                                const isEditing  = editingId === msg.id;
                                const reakcije   = msg.reakcije || [];
                                const meniOtvoren = ctxMeni?.msg?.id === msg.id || punIzbor?.msg?.id === msg.id;

                                // Sistemska poruka (dodavanje/uklanjanje/napuštanje) — centrirano obaveštenje
                                if (msg.tip === 'system') {
                                    return (
                                        <React.Fragment key={msg.id}>
                                            {showDate && (
                                                <div className="chat-date-sep"><span>{formatDate(msg.datum)}</span></div>
                                            )}
                                            <div className="chat-system-msg" id={`chat-msg-${msg.id}`}>
                                                <span>{msg.sadrzaj}</span>
                                            </div>
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <React.Fragment key={msg.id}>
                                        {showDate && (
                                            <div className="chat-date-sep">
                                                <span>{formatDate(msg.datum)}</span>
                                            </div>
                                        )}
                                        <div
                                            id={`chat-msg-${msg.id}`}
                                            className={`chat-msg-wrap ${isMine ? 'sent' : 'recv'}${meniOtvoren ? ' meni-otvoren' : ''}${highlightMsgId === msg.id ? ' highlight' : ''}${izbor ? ' u-izboru' : ''}${izbor?.includes(msg.id) ? ' izabrana' : ''}`}
                                            /* U režimu izbora klik bilo gde na poruci je bira — capture faza
                                               presreće i slike i linkove, koji bi inače otvorili pregled. */
                                            onClickCapture={izbor ? (e) => {
                                                e.preventDefault(); e.stopPropagation();
                                                if (!msg.obrisan) prebaciIzbor(msg.id);
                                            } : undefined}
                                            onContextMenu={(e) => otvoriMeni(e, msg)}
                                            onTouchStart={(e) => pocniDugiPritisak(e, msg)}
                                            onTouchMove={prekiniDugiPritisak}
                                            onTouchEnd={prekiniDugiPritisak}
                                            onTouchCancel={prekiniDugiPritisak}
                                        >

                                            {/* Avatar pošiljaoca — samo u grupama, za leve/recv poruke (moje poruke nemaju) */}
                                            {!isMine && activeConv.tip === 2 && (
                                                <div className="chat-msg-avatar">
                                                    {showAvatar && (
                                                        <ChatAvatar name={msg.ime_korisnika} avatar={msg.avatar_korisnika} size={30} onExpand={openImgPreview} />
                                                    )}
                                                </div>
                                            )}

                                            {izbor && !msg.obrisan && (
                                                <span className={`chat-izbor-kruzic${izbor.includes(msg.id) ? ' on' : ''}`}>
                                                    <i className="pi pi-check" />
                                                </span>
                                            )}
                                            {/* Radnje nad porukom su u meniju na desni klik (dugi pritisak na telefonu) — vidi `ctxMeni` */}
                                            <div className="chat-bubble">
                                                {showName && (
                                                    <div className="chat-bubble-sender" style={{ color: avatarColor(msg.ime_korisnika) }}>
                                                        {msg.ime_korisnika}
                                                    </div>
                                                )}

                                                {!!msg.prosledjeno && !msg.obrisan && (
                                                    <div className="chat-prosledjeno">
                                                        <i className="pi pi-share-alt" /> {t('chat.prosledjeno')}
                                                    </div>
                                                )}

                                                {msg.reply_preview && !msg.obrisan && (
                                                    <div
                                                        className="chat-quote"
                                                        onClick={(e) => { e.stopPropagation(); scrollToMessage(msg.reply_to_id); }}
                                                        title={t('chat.goToOriginal')}
                                                    >
                                                        <div className="chat-quote-name">{msg.reply_preview.ime}</div>
                                                        <div className="chat-quote-text">{msg.reply_preview.sadrzaj}</div>
                                                    </div>
                                                )}

                                                {/* Deleted / edit / normal content */}
                                                {msg.obrisan ? (
                                                    <div className="chat-bubble-deleted">
                                                        <i className="pi pi-ban" /> {t('chat.messageDeleted')}
                                                    </div>
                                                ) : isEditing ? (
                                                    <div className="chat-edit-wrap" onClick={e => e.stopPropagation()}>
                                                        <textarea
                                                            className="chat-edit-input"
                                                            value={editingText}
                                                            onChange={e => setEditingText(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                                                                if (e.key === 'Escape') cancelEdit();
                                                            }}
                                                            autoFocus
                                                        />
                                                        <div className="chat-edit-hints">
                                                            Esc da <span onClick={cancelEdit}>otkaži</span> · Enter da <span onClick={saveEdit}>sačuvaš</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {msg.tip === 'text' && (
                                                            <div className="chat-bubble-text">
                                                                {renderText(msg.sadrzaj, showSearch ? searchQuery : '', msg.id === currentMatchId)}
                                                                {!!msg.izmenjeno && <span className="chat-edited-tag"> (izmenjeno)</span>}
                                                            </div>
                                                        )}
                                                        {msg.tip === 'image' && msg.file_path && (
                                                            <img
                                                                src={fileUrl(msg.file_path)}
                                                                alt={msg.file_naziv}
                                                                className="chat-bubble-img"
                                                                onClick={(e) => { e.stopPropagation(); openImgPreview(fileUrl(msg.file_path)); }}
                                                            />
                                                        )}
                                                        {msg.tip === 'gif' && msg.sadrzaj && (
                                                            <img
                                                                src={msg.sadrzaj}
                                                                alt="GIF"
                                                                className="chat-bubble-gif"
                                                                onClick={(e) => { e.stopPropagation(); openImgPreview(msg.sadrzaj); }}
                                                            />
                                                        )}
                                                        {msg.tip === 'poll' && msg.poll_data && (
                                                            <div className="chat-poll-card" onClick={e => e.stopPropagation()}>
                                                                <div className="chat-poll-question">{msg.poll_data.pitanje}</div>
                                                                <div className="chat-poll-options">
                                                                    {(msg.poll_data.opcije || []).map(o => {
                                                                        const ukupno  = msg.poll_data.ukupno_glasova || 0;
                                                                        const pct     = ukupno > 0 ? Math.round((o.glasova / ukupno) * 100) : 0;
                                                                        const selected = o.ja_glasao === 1;
                                                                        return (
                                                                            <button
                                                                                key={o.id}
                                                                                className={`chat-poll-option${selected ? ' voted' : ''}`}
                                                                                onClick={() => votePoll(msg, o.id)}
                                                                                title={selected ? 'Tvoj glas' : 'Glasaj'}
                                                                            >
                                                                                <div className="chat-poll-bar" style={{ width: `${pct}%` }} />
                                                                                <span className="chat-poll-opt-text">{o.opcija}</span>
                                                                                <span className="chat-poll-opt-pct">{pct}%</span>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <div className="chat-poll-footer">
                                                                    📊 {msg.poll_data.ukupno_glasova || 0} glasova
                                                                </div>
                                                            </div>
                                                        )}
                                                        {msg.tip === 'event' && msg.event_data && (
                                                            <div className="chat-event-card" onClick={e => e.stopPropagation()}>
                                                                <div className="chat-event-header">
                                                                    <i className="pi pi-calendar" />
                                                                    <span>{t('chat.event')}</span>
                                                                </div>
                                                                <div className="chat-event-naziv">{msg.event_data.naziv}</div>
                                                                <div className="chat-event-meta">
                                                                    <i className="pi pi-clock" />
                                                                    {(() => {
                                                                        const d = new Date(msg.event_data.datum_vreme);
                                                                        return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}. ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                                                                    })()}
                                                                </div>
                                                                {msg.event_data.opis && (
                                                                    <div className="chat-event-opis">{msg.event_data.opis}</div>
                                                                )}
                                                                <div className="chat-event-kreirao">Kreirao: {msg.event_data.kreirao}</div>
                                                            </div>
                                                        )}
                                                        {msg.tip === 'file' && msg.file_path && (
                                                            <a
                                                                className="chat-bubble-file"
                                                                href={fileUrl(msg.file_path)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <i className="pi pi-file" />
                                                                <span>{msg.file_naziv || 'Fajl'}</span>
                                                            </a>
                                                        )}
                                                        {msg.tip === 'location' && msg.sadrzaj && (() => {
                                                            const [lat, lng] = msg.sadrzaj.split(',').map(Number);
                                                            return (
                                                                <a
                                                                    className="chat-bubble-location"
                                                                    href={`https://www.google.com/maps?q=${lat},${lng}`}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <i className="pi pi-map-marker" />
                                                                    <span>{lat.toFixed(5)}, {lng.toFixed(5)}</span>
                                                                </a>
                                                            );
                                                        })()}
                                                        {msg.tip === 'audio' && msg.file_path && (
                                                            <audio
                                                                className="chat-bubble-audio"
                                                                controls
                                                                src={fileUrl(msg.file_path)}
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        )}
                                                        {msg.tip === 'video' && msg.file_path && (
                                                            <video
                                                                className="chat-bubble-video"
                                                                controls
                                                                src={fileUrl(msg.file_path)}
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        )}
                                                        <span className="chat-bubble-time">
                                                            {!!msg.sacuvana && <i className="pi pi-star-fill chat-bubble-zvezda" title={t('chat.sacuvana')} />}
                                                            {zakacenSet.has(msg.id) && <i className="pi pi-thumbtack chat-bubble-pin" title={t('chat.zakacenaPoruka')} />}
                                                            {formatTime(msg.datum)}
                                                            {isMine && (() => {
                                                                const isGroup = activeConv.tip === 2;
                                                                const total   = isGroup ? (activeConv.broj_clanova - 1) : 1;
                                                                const count   = msg.procitano_count || 0;
                                                                const isRead  = count >= 1;
                                                                return (
                                                                    <span className={`chat-receipt${isRead ? ' read' : ''}`}>
                                                                        <i className="pi pi-check" />
                                                                        <i className="pi pi-check" />
                                                                        {isGroup && (
                                                                            <span
                                                                                className="chat-receipt-count clickable"
                                                                                onClick={(e) => openReadersPopup(e, msg)}
                                                                                title={t('chat.whoRead')}
                                                                            >
                                                                                {count}/{total}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </span>
                                                    </>
                                                )}

                                                {/* Reaction pills (not shown for deleted) */}
                                                {!msg.obrisan && reakcije.length > 0 && (
                                                    <div className="chat-reactions">
                                                        {reakcije.map(r => (
                                                            <button
                                                                key={r.emoji}
                                                                className={`chat-reaction-pill${r.mine ? ' mine' : ''}`}
                                                                onClick={(e) => { e.stopPropagation(); sendReaction(msg.id, msg.id_grupe, r.emoji); }}
                                                                title={r.mine ? 'Klikni da ukloniš' : 'Klikni da dodaš'}
                                                            >
                                                                {r.emoji}{r.cnt > 1 && <span>{r.cnt}</span>}
                                                            </button>
                                                        ))}
                                                        {/* Dodaj još jednu reakciju — bilo koji emotikon iz punog izbora */}
                                                        <button
                                                            className="chat-reaction-pill chat-reaction-add"
                                                            onClick={(e) => otvoriPunIzbor(e, msg)}
                                                            title={t('chat.dodajReakciju')}
                                                        >
                                                            <i className="pi pi-plus" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}

                            {/* Typing indicator */}
                            {typingLabel && (
                                <div className="chat-msg-wrap recv">
                                    <div className="chat-bubble">
                                        <div style={{ fontSize: '0.72rem', color: '#667781', marginBottom: '3px' }}>{typingLabel} {t('chat.typing')}</div>
                                        <div className="chat-typing">
                                            <span/><span/><span/>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {showScrollDown && (
                            <button className="chat-scroll-down" onClick={scrollToBottom} title={t('chat.scrollToBottom')}>
                                <i className="pi pi-chevron-down" />
                            </button>
                        )}

                        {/* Izaberi više — traka sa radnjama umesto polja za pisanje */}
                        {izbor && (() => {
                            const izabrane = izabranePoruke();
                            const n = izabrane.length;
                            const imaTeksta = izabrane.some(m => m.tip === 'text' && m.sadrzaj && !m.obrisan);
                            const prosledive = izabrane.filter(mozeProsledjivanje);
                            const moje = izabrane.filter(m => m.id_korisnik === myId && (m.tip_korisnika || 'OPERATER') === 'OPERATER' && !m.obrisan);
                            const sveSacuvane = n > 0 && izabrane.every(m => m.sacuvana);
                            return (
                                <div className="chat-izbor-traka" onClick={e => e.stopPropagation()}>
                                    <button className="chat-izbor-btn" onClick={() => setIzbor(null)} title={t('common.cancel')}>
                                        <i className="pi pi-times" />
                                    </button>
                                    <span className="chat-izbor-broj">{t('chat.izabrano', { broj: n })}</span>
                                    <div className="chat-izbor-akcije">
                                        <button className="chat-izbor-btn" disabled={!imaTeksta} onClick={kopirajIzabrane} title={t('chat.kopiraj')}>
                                            <i className="pi pi-copy" />
                                        </button>
                                        <button className="chat-izbor-btn" disabled={!n} onClick={() => sacuvajPoruke(izbor, !sveSacuvane).then(() => setIzbor(null))}
                                            title={sveSacuvane ? t('chat.ukloniIzSacuvanih') : t('chat.sacuvaj')}>
                                            <i className={`pi ${sveSacuvane ? 'pi-star-fill' : 'pi-star'}`} />
                                        </button>
                                        <button className="chat-izbor-btn" disabled={!prosledive.length} onClick={() => otvoriProsledi(prosledive)} title={t('chat.prosledi')}>
                                            <i className="pi pi-share-alt" />
                                        </button>
                                        {/* Brisanje samo kad su SVE izabrane moje — kao WhatsApp
                                            „obriši za sve"; tuđe poruke niko ne može da obriše. */}
                                        <button
                                            className="chat-izbor-btn opasno"
                                            disabled={!moje.length || moje.length !== n}
                                            onClick={obrisiIzabrane}
                                            title={t('common.delete')}
                                        >
                                            <i className="pi pi-trash" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Input area */}
                        <div className={`chat-input-area${izbor ? ' skriveno' : ''}`} onClick={e => e.stopPropagation()}>
                            {replyingTo && (
                                <div className="chat-reply-bar">
                                    <div className="chat-reply-bar-content">
                                        <span className="chat-reply-bar-name">{replyingTo.ime_korisnika}</span>
                                        <span className="chat-reply-bar-text">
                                            {replyingTo.tip === 'text'
                                                ? (replyingTo.sadrzaj || '').slice(0, 80)
                                                : replyingTo.tip === 'image' ? '📷 Slika'
                                                : replyingTo.tip === 'gif'   ? '🎬 GIF'
                                                : replyingTo.tip === 'audio' ? '🎤 Glasovna poruka'
                                                : replyingTo.tip === 'video' ? '🎥 Video'
                                                : replyingTo.tip === 'location' ? '📍 Lokacija'
                                                : replyingTo.tip === 'poll'  ? '📊 Anketa'
                                                : replyingTo.tip === 'event' ? '📅 Event'
                                                : '📎 Fajl'}
                                        </span>
                                    </div>
                                    <button className="chat-reply-bar-close" onClick={() => setReplyingTo(null)}>
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            )}
                            {pastedImage && (
                                <div className="chat-paste-preview">
                                    <img src={pastedImage.preview} alt="Nalepljena slika" />
                                    <button className="chat-paste-remove" onClick={removePastedImage} title={t('chat.remove')}>
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            )}
                            <div className="chat-input-row">
                                {/* Emoji */}
                                <button className="chat-input-btn" onClick={() => setShowEmoji(s => !s)} title={t('chat.emoji')}>
                                    😊
                                </button>
                                {showEmoji && (
                                    <div className="chat-emoji-picker">
                                        <Picker
                                            data={data}
                                            onEmojiSelect={insertEmoji}
                                            theme={theme}
                                            locale="en"
                                            previewPosition="none"
                                        />
                                    </div>
                                )}

                                {/* App launcher */}
                                <div className="chat-app-launcher-wrap">
                                    <button
                                        className="chat-input-btn"
                                        onClick={(e) => { e.stopPropagation(); setShowAppLauncher(s => !s); }}
                                        title={t('chat.apps')}
                                        disabled={uploadingFile}
                                    >
                                        {uploadingFile
                                            ? <i className="pi pi-spin pi-spinner" />
                                            : <i className="pi pi-plus-circle" />}
                                    </button>
                                    {showAppLauncher && (
                                        <div className="chat-app-menu" onClick={e => e.stopPropagation()}>
                                            <button className="chat-app-item" onClick={() => { setShowAppLauncher(false); fileInputRef.current?.click(); }}>
                                                <i className="pi pi-paperclip" /><span>{t('chat.file')}</span>
                                            </button>
                                            <button className="chat-app-item" onClick={() => { setShowAppLauncher(false); cameraInputRef.current?.click(); }}>
                                                <i className="pi pi-camera" /><span>{t('chat.camera')}</span>
                                            </button>
                                            <button className="chat-app-item" onClick={openGifModal}>
                                                <span className="chat-app-gif-icon">GIF</span><span>GIF</span>
                                            </button>
                                            <button className="chat-app-item" onClick={openLocationPicker}>
                                                <i className="pi pi-map-marker" /><span>{t('chat.location')}</span>
                                            </button>
                                            <button className="chat-app-item" onClick={openPollModal}>
                                                <i className="pi pi-chart-bar" /><span>{t('chat.poll')}</span>
                                            </button>
                                            <button className="chat-app-item" onClick={openEventModal}>
                                                <i className="pi pi-calendar-plus" /><span>{t('chat.event')}</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />

                                {/* Text input */}
                                <div
                                    ref={inputRef}
                                    className="chat-input-wrap"
                                    contentEditable
                                    suppressContentEditableWarning
                                    data-placeholder={t('chat.writeMessagePh')}
                                    onKeyDown={handleKeyDown}
                                    onInput={handleInput}
                                    onPaste={handlePaste}
                                />

                                {/* Send */}
                                <button className="chat-send-btn" onClick={sendMessage} title={t('chat.send')}>
                                    <i className="pi pi-send" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ── GIF modal ── */}
            {showGifModal && (
                <div className="chat-modal-overlay" onClick={() => setShowGifModal(false)}>
                    <div className="chat-gif-modal" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3>{t('chat.chooseGif')}</h3>
                            <button className="chat-modal-close" onClick={() => setShowGifModal(false)}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-gif-search">
                            <input
                                autoFocus
                                placeholder={t('chat.searchGifsPh')}
                                value={gifSearch}
                                onChange={handleGifSearchChange}
                            />
                        </div>
                        <div className="chat-gif-grid">
                            {gifLoading ? (
                                <div className="chat-gif-loading"><i className="pi pi-spin pi-spinner" /></div>
                            ) : gifResults.length === 0 ? (
                                <div className="chat-gif-empty">{t('chat.noResults')}</div>
                            ) : (
                                gifResults.map(g => (
                                    <img
                                        key={g.id}
                                        src={g.preview || g.url}
                                        alt={g.title}
                                        className="chat-gif-thumb"
                                        onClick={() => sendGif(g.url)}
                                        title={g.title}
                                    />
                                ))
                            )}
                        </div>
                        <div className="chat-gif-powered">
                            Powered by GIPHY
                        </div>
                    </div>
                </div>
            )}

            {/* ── Poll modal ── */}
            {showPollModal && (
                <div className="chat-modal-overlay" onClick={() => setShowPollModal(false)}>
                    <div className="chat-modal" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3>{t('chat.createPoll')}</h3>
                            <button className="chat-modal-close" onClick={() => setShowPollModal(false)}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-modal-body">
                            <input
                                className="chat-modal-input"
                                placeholder={t('chat.pollQuestionPh')}
                                value={pollQuestion}
                                onChange={e => setPollQuestion(e.target.value)}
                                maxLength={250}
                            />
                            {pollOptions.map((opt, i) => (
                                <div key={i} className="chat-poll-option-row">
                                    <input
                                        className="chat-modal-input"
                                        placeholder={`Opcija ${i + 1}`}
                                        value={opt}
                                        onChange={e => setPollOption(i, e.target.value)}
                                        maxLength={150}
                                    />
                                    {pollOptions.length > 2 && (
                                        <button className="chat-poll-remove-opt" onClick={() => removePollOption(i)}>
                                            <i className="pi pi-times" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {pollOptions.length < 6 && (
                                <button className="chat-poll-add-opt" onClick={addPollOption}>
                                    <i className="pi pi-plus" /> {t('chat.addOption')}
                                </button>
                            )}
                        </div>
                        <div className="chat-modal-footer">
                            <button className="chat-modal-btn-cancel" onClick={() => setShowPollModal(false)}>{t('common.cancel')}</button>
                            <button
                                className="chat-modal-btn-create"
                                onClick={submitPoll}
                                disabled={!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
                            >
                                Kreiraj anketu
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLocationModal && (
                <LocationPickerModal
                    onConfirm={confirmLocation}
                    onClose={() => setShowLocationModal(false)}
                />
            )}

            {/* ── Event modal ── */}
            {showEventModal && (
                <div className="chat-modal-overlay" onClick={() => setShowEventModal(false)}>
                    <div className="chat-modal" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3>{t('chat.createEvent')}</h3>
                            <button className="chat-modal-close" onClick={() => setShowEventModal(false)}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-modal-body">
                            <label className="chat-modal-label">{t('chat.eventName')}</label>
                            <input
                                className="chat-modal-input"
                                placeholder={t('chat.eventNamePh')}
                                value={eventNaziv}
                                onChange={e => setEventNaziv(e.target.value)}
                                maxLength={200}
                            />
                            <label className="chat-modal-label" style={{ marginTop: 12 }}>{t('chat.dateTime')}</label>
                            <input
                                className="chat-modal-input"
                                type="datetime-local"
                                value={eventDatum}
                                onChange={e => setEventDatum(e.target.value)}
                            />
                            <label className="chat-modal-label" style={{ marginTop: 12 }}>{t('chat.description')}</label>
                            <textarea
                                className="chat-modal-input"
                                placeholder={t('chat.descriptionPh')}
                                value={eventOpis}
                                onChange={e => setEventOpis(e.target.value)}
                                maxLength={500}
                                rows={3}
                            />
                        </div>
                        <div className="chat-modal-footer">
                            <button className="chat-modal-btn-cancel" onClick={() => setShowEventModal(false)}>{t('common.cancel')}</button>
                            <button
                                className="chat-modal-btn-create"
                                onClick={submitEvent}
                                disabled={!eventNaziv.trim() || !eventDatum}
                            >
                                Kreiraj event
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── New chat modal ── */}
            {showNewModal && (
                <div className="chat-modal-overlay" onClick={() => setShowNewModal(false)}>
                    <div className="chat-modal" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3>{t('chat.newConversation')}</h3>
                            <button className="chat-modal-close" onClick={() => setShowNewModal(false)}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-modal-body">
                            {/* 1. S KIM — publika odredjuje ceo spisak ispod, pa ide prva.
                                Izbor se ZADRZAVA pri prebacivanju, tako da se grupa moze
                                sastaviti i od operatera i od mobilnih. */}
                            <div className="chat-modal-segment" data-izbor={modalCategory}>
                                <button
                                    type="button"
                                    className={`chat-modal-segment-btn${modalCategory === 'OPERATER' ? ' active' : ''}`}
                                    onClick={() => setModalCategory('OPERATER')}
                                >
                                    <i className="pi pi-desktop" />
                                    {t('admin.tabOperateri')}
                                    <span className="chat-modal-segment-count">{brojPoTipu('OPERATER')}</span>
                                </button>
                                <button
                                    type="button"
                                    className={`chat-modal-segment-btn${modalCategory === 'MOBILNI' ? ' active' : ''}`}
                                    onClick={() => setModalCategory('MOBILNI')}
                                >
                                    <i className="pi pi-mobile" />
                                    {t('admin.tabMobilniOperateri')}
                                    <span className="chat-modal-segment-count">{brojPoTipu('MOBILNI')}</span>
                                </button>
                            </div>

                            {/* 2. KAKO — menjanje rezima BRISE izbor, zato je odvojeno
                                i vizuelno jace od filtera iznad. */}
                            <div className="chat-modal-mode">
                                <button
                                    type="button"
                                    className={`chat-modal-mode-btn${modalTab === 'dm' ? ' active' : ''}`}
                                    onClick={() => { setModalTab('dm'); setSelectedUsers([]); }}
                                >
                                    <i className="pi pi-comment" />
                                    <span>{t('chat.directMessage')}</span>
                                </button>
                                <button
                                    type="button"
                                    className={`chat-modal-mode-btn${modalTab === 'group' ? ' active' : ''}`}
                                    onClick={() => { setModalTab('group'); setSelectedUsers([]); }}
                                >
                                    <i className="pi pi-users" />
                                    <span>{t('chat.newGroup')}</span>
                                </button>
                            </div>

                            {modalTab === 'group' && (
                                <div className="chat-modal-group-name">
                                    <input
                                        placeholder={t('chat.groupNamePh')}
                                        value={groupName}
                                        onChange={e => setGroupName(e.target.value)}
                                    />
                                </div>
                            )}
                            <div className="chat-modal-search">
                                <input
                                    placeholder={t('chat.searchUsersPh')}
                                    value={modalSearch}
                                    onChange={e => setModalSearch(e.target.value)}
                                />
                            </div>
                            <div className="chat-modal-user-list">
                                {filteredUsers.map(u => {
                                    const key      = ukey(u.tip, u.value);
                                    const sel      = selectedUsers.includes(key);
                                    const canSelect = modalTab === 'group' || !selectedUsers.length || sel;
                                    return (
                                        <div
                                            key={key}
                                            className={`chat-modal-user${sel ? ' selected' : ''}`}
                                            onClick={() => (modalTab === 'group' || canSelect) && toggleSelectUser(u.value, u.tip)}
                                            style={{ opacity: !canSelect ? 0.4 : 1 }}
                                        >
                                            <ChatAvatar name={u.label} avatar={u.avatar} online={tackaZa(u.tip, u.value)} size={36} onExpand={openImgPreview} />
                                            <div style={{ flex: 1 }}>
                                                <div className="chat-modal-user-name">{u.label}</div>
                                                <div className="chat-modal-user-email">{u.tip === 'MOBILNI' ? t(MOB_ROLA_KEY[u.rola] || '') : u.email}</div>
                                                {oznakaPrisustva(stanjeKorisnika(u.tip, u.value, u.poslednja_aktivnost), { kompaktno: true })}
                                            </div>
                                            <div className="chat-modal-user-check">
                                                {sel && <i className="pi pi-check" />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="chat-modal-footer">
                            <button className="btn-cancel" onClick={() => setShowNewModal(false)}>{t('common.cancel')}</button>
                            <button
                                className="btn-create"
                                onClick={createChat}
                                disabled={!selectedUsers.length || (modalTab === 'group' && !groupName.trim())}
                            >
                                {modalTab === 'dm' ? t('chat.openConversation') : t('chat.createGroup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Settings modal ── */}
            {showSettings && (
                <div className="chat-modal-overlay" onClick={closeSettings}>
                    <div className="chat-modal chat-settings-modal" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3>{t('chat.settings')}</h3>
                            <button className="chat-modal-close" onClick={closeSettings}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-modal-body">
                            {/* Avatar */}
                            <div className="chat-settings-section">
                                <div className="chat-settings-label">{t('chat.profilePicture')}</div>
                                <div className="chat-settings-avatar-row">
                                    <ChatAvatar name={myName} avatar={myAvatar} size={72} onExpand={openImgPreview} />
                                    <div className="chat-settings-avatar-actions">
                                        <button
                                            className="chat-settings-btn"
                                            onClick={() => avatarInputRef.current?.click()}
                                            disabled={avatarUploading}
                                        >
                                            {avatarUploading
                                                ? <><i className="pi pi-spin pi-spinner" /> Otpremam...</>
                                                : <><i className="pi pi-camera" /> {myAvatar ? 'Promeni sliku' : 'Dodaj sliku'}</>}
                                        </button>
                                        <span className="chat-settings-hint">{t('chat.avatarHint')}</span>
                                    </div>
                                </div>
                                <input
                                    ref={avatarInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleAvatarSelect}
                                />
                            </div>

                            {/* Theme */}
                            <div className="chat-settings-section">
                                <div className="chat-settings-label">{t('chat.appearance')}</div>
                                {/* Tri kartice sa umanjenim chatom u svakoj temi — izbor se vidi
                                    pre nego što se klikne. Klik na već izabranu ne radi ništa. */}
                                <div className="chat-tema-kartice" role="radiogroup" aria-label={t('chat.appearance')}>
                                    {['light', 'dark', 'retro', 'retrodark'].map(tema => {
                                        const aktivna = theme === tema;
                                        return (
                                            <button
                                                key={tema}
                                                type="button"
                                                role="radio"
                                                aria-checked={aktivna}
                                                className={`chat-tema-kartica ${tema}${aktivna ? ' aktivna' : ''}`}
                                                onClick={(e) => { if (!aktivna) postaviTemu(tema, e); }}
                                            >
                                                <span className="chat-tema-mini" aria-hidden="true">
                                                    <span className="mini-zaglavlje"><span className="mini-avatar" /><span className="mini-ime" /></span>
                                                    <span className="mini-poruka levo" />
                                                    <span className="mini-poruka desno" />
                                                    <span className="mini-poruka levo kratka" />
                                                    <span className="mini-unos" />
                                                </span>
                                                <span className="chat-tema-naziv">
                                                    <i className={`pi ${tema.startsWith('retro') ? 'pi-desktop' : tema === 'dark' ? 'pi-moon' : 'pi-sun'}`} />
                                                    {tema === 'retro' ? t('korisnik.retroModSvetli')
                                                        : tema === 'retrodark' ? t('korisnik.retroModTamni')
                                                        : tema === 'dark' ? t('korisnik.tamniMod') : t('korisnik.svetliMod')}
                                                </span>
                                                <span className="chat-tema-kvacica"><i className="pi pi-check" /></span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="chat-tema-red">
                                    <div className="chat-tema-red-tekst">
                                        <span className="chat-tema-red-naslov">
                                            {theme === 'retro' ? t('korisnik.retroModSvetli')
                                                : theme === 'retrodark' ? t('korisnik.retroModTamni')
                                                : theme === 'dark' ? t('korisnik.tamniMod') : t('korisnik.svetliMod')}
                                        </span>
                                        <span className="chat-tema-red-opis">{t('chat.temaCelaAplikacija')}</span>
                                    </div>
                                    <TemaPrekidac dark={theme === 'dark'} onToggle={(e) => toggleTheme(e)} ariaLabel={t('chat.appearance')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Group info panel ── */}
            <div className={`chat-group-panel${showGroupPanel ? ' open' : ''}`}>
                <div className="chat-group-panel-header">
                    <span>{t('chat.groupInfo')}</span>
                    <button onClick={closeGroupPanel}>
                        <i className="pi pi-times" />
                    </button>
                </div>
                {groupInfo && (
                    <div className="chat-group-panel-body">
                        <div className="chat-group-avatar-wrap">
                            <ChatAvatar name={groupInfo.naziv} avatar={groupInfo.avatar} isGroup size={76} onExpand={openImgPreview} />
                            {isGroupAdmin && (
                                <button
                                    className="chat-group-avatar-edit"
                                    onClick={() => groupAvatarInputRef.current?.click()}
                                    disabled={groupAvatarUploading}
                                    title={t('chat.changePhoto')}
                                >
                                    <i className={groupAvatarUploading ? 'pi pi-spin pi-spinner' : 'pi pi-camera'} />
                                </button>
                            )}
                            <input ref={groupAvatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleGroupAvatarSelect} />
                        </div>

                        {groupNameEditing ? (
                            <div className="chat-group-name-edit">
                                <input
                                    value={groupNameInput}
                                    onChange={e => setGroupNameInput(e.target.value)}
                                    autoFocus
                                    onKeyDown={e => { if (e.key === 'Enter') saveGroupName(); if (e.key === 'Escape') setGroupNameEditing(false); }}
                                />
                                <button className="chat-group-name-ok" onClick={saveGroupName} title={t('common.save')}><i className="pi pi-check" /></button>
                                <button className="chat-group-name-cancel" onClick={() => setGroupNameEditing(false)} title={t('common.cancel')}><i className="pi pi-times" /></button>
                            </div>
                        ) : (
                            <div className="chat-group-panel-name">
                                {groupInfo.naziv}
                                {isGroupAdmin && (
                                    <button
                                        className="chat-group-name-edit-btn"
                                        onClick={() => { setGroupNameInput(groupInfo.naziv || ''); setGroupNameEditing(true); }}
                                        title={t('chat.renameGroup')}
                                    >
                                        <i className="pi pi-pencil" />
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="chat-group-panel-meta">
                            <i className="pi pi-user" />
                            {t('chat.creator')} <strong>{groupInfo.kreirao}</strong>
                        </div>
                        <div className="chat-group-panel-meta">
                            <i className="pi pi-calendar" />
                            {t('chat.created')} <strong>{new Date(groupInfo.kreirana).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
                        </div>
                        <div className="chat-group-panel-section">
                            {t('chat.participants')} ({groupInfo.clanovi.length})
                            {brojNaMrezi(groupInfo.clanovi) > 0 && (
                                <span className="chat-online-count">
                                    {t('chat.onlineCount', { broj: brojNaMrezi(groupInfo.clanovi) })}
                                </span>
                            )}
                        </div>
                        <div className="chat-group-panel-members">
                            {groupInfo.clanovi.map(c => (
                                <div key={ukey(c.tip, c.id_user)} className="chat-group-panel-member">
                                    <ChatAvatar name={c.ime} avatar={c.avatar} online={tackaZa(c.tip, c.id_user)} size={36} style={{ flexShrink: 0 }} onExpand={openImgPreview} />
                                    <div className="chat-group-panel-member-info">
                                        <div className="chat-group-panel-member-name">{c.ime}</div>
                                        {c.uloga === 2 && <span className="chat-group-panel-admin">{t('chat.admin')}</span>}
                                        {c.tip === 'MOBILNI' && <span className="chat-group-panel-admin">{t(MOB_ROLA_KEY[c.rola] || '')}</span>}
                                        {/* Ja sam uvek tu — za sebe se stanje ne piše */}
                                        {!(c.id_user === myId && (c.tip || 'OPERATER') === 'OPERATER') &&
                                            oznakaPrisustva(stanjeKorisnika(c.tip, c.id_user, c.poslednja_aktivnost), { kompaktno: true })}
                                    </div>
                                    {isGroupAdmin && c.uloga === 1 && (
                                        <button
                                            className="chat-group-panel-remove-btn"
                                            onClick={() => removeGroupMember(c.id_user, c.tip)}
                                            title={t('chat.removeMember')}
                                        >
                                            <i className="pi pi-times" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* {t('chat.leaveGroup')} */}
                        <button className="chat-group-panel-leave-btn" onClick={leaveGroup}>
                            <i className="pi pi-sign-out" /> {t('chat.leaveGroup')}
                        </button>

                        {/* {t('chat.addMember')} */}
                        {!addMemberMode ? (
                            <button className="chat-group-panel-add-btn" onClick={() => { setAddMemberMode(true); setAddMemberCategory('OPERATER'); }}>
                                <i className="pi pi-user-plus" /> {t('chat.addMember')}
                            </button>
                        ) : (
                            <div className="chat-group-panel-add-wrap">
                                <div className="chat-modal-tabs">
                                    <button
                                        className={`chat-modal-tab${addMemberCategory === 'OPERATER' ? ' active' : ''}`}
                                        onClick={() => setAddMemberCategory('OPERATER')}
                                    >{t('admin.tabOperateri')}</button>
                                    <button
                                        className={`chat-modal-tab${addMemberCategory === 'MOBILNI' ? ' active' : ''}`}
                                        onClick={() => setAddMemberCategory('MOBILNI')}
                                    >{t('admin.tabMobilniOperateri')}</button>
                                </div>
                                <input
                                    className="chat-group-panel-add-input"
                                    placeholder={t('chat.searchUsersPh')}
                                    value={addMemberSearch}
                                    onChange={e => setAddMemberSearch(e.target.value)}
                                    autoFocus
                                />
                                <div className="chat-group-panel-add-list">
                                    {addableUsers.length === 0 && (
                                        <div className="chat-group-panel-add-empty">{t('chat.noAvailableUsers')}</div>
                                    )}
                                    {addableUsers.map(u => (
                                        <div key={ukey(u.tip, u.value)} className="chat-group-panel-add-user" onClick={() => addGroupMember(u.value, u.tip)}>
                                            <ChatAvatar name={u.label} avatar={u.avatar} size={32} style={{ flexShrink: 0 }} onExpand={openImgPreview} />
                                            <div>
                                                <div style={{ fontSize: '0.85rem', color: '#111b21' }}>{u.label}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#54656f' }}>{u.tip === 'MOBILNI' ? t(MOB_ROLA_KEY[u.rola] || '') : u.email}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button className="chat-group-panel-cancel-btn" onClick={() => { setAddMemberMode(false); setAddMemberSearch(''); }}>
                                    Otkaži
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {showGroupPanel && <div className="chat-group-panel-overlay" onClick={closeGroupPanel} />}

            {/* ── Info panel (datum, učesnici, media) ── */}
            <div className={`chat-info-panel${showInfoPanel ? ' open' : ''}`}>
                <div className="chat-info-panel-header">
                    <span>{showMediaGallery ? `${t('chat.images')} (${chatImages.length})` : t('chat.info')}</span>
                    <button onClick={showMediaGallery ? () => setShowMediaGallery(false) : closeInfoPanel}>
                        <i className={`pi ${showMediaGallery ? 'pi-arrow-left' : 'pi-times'}`} />
                    </button>
                </div>

                {showMediaGallery ? (
                    <div className="chat-info-gallery">
                        {chatImages.length === 0
                            ? <div className="chat-info-empty">{t('chat.noImages')}</div>
                            : chatImages.map((url, i) => (
                                <img key={i} src={url} alt="" className="chat-info-gallery-img" onClick={() => openImgPreview(url)} />
                              ))}
                    </div>
                ) : (
                    activeConv && (
                        <div className="chat-info-panel-body">
                            <div className="chat-info-hero">
                                <ChatAvatar
                                    name={activeConv.naziv_prikaz || activeConv.naziv || ''}
                                    avatar={activeConv.tip === 2 ? null : activeConv.dm_avatar}
                                    isGroup={activeConv.tip === 2}
                                    size={72}
                                    onExpand={openImgPreview}
                                />
                                <div className="chat-info-name">{activeConv.naziv_prikaz || activeConv.naziv || 'Razgovor'}</div>
                            </div>

                            {infoData?.kreirana && (
                                <div className="chat-info-row">
                                    <i className="pi pi-calendar" />
                                    <span>{t('chat.created')} <strong>{new Date(infoData.kreirana).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong></span>
                                </div>
                            )}

                            <div className="chat-info-section">
                                {t('chat.participants')} ({infoData?.clanovi?.length || 0})
                                {brojNaMrezi(infoData?.clanovi) > 0 && (
                                    <span className="chat-online-count">
                                        {t('chat.onlineCount', { broj: brojNaMrezi(infoData?.clanovi) })}
                                    </span>
                                )}
                            </div>
                            <div className="chat-info-members">
                                {/* key nosi i tip: auth.Users.id i chat.MobilniKorisnik.id
                                    mogu numericki da se poklope, pa bi sam id_user udvojio key. */}
                                {(infoData?.clanovi || []).map(c => (
                                    <div key={ukey(c.tip, c.id_user)} className="chat-info-member">
                                        <ChatAvatar name={c.ime} avatar={c.avatar} online={tackaZa(c.tip, c.id_user)} size={36} style={{ flexShrink: 0 }} onExpand={openImgPreview} />
                                        <div className="chat-info-member-text">
                                            <span className="chat-info-member-name">{c.ime}</span>
                                            {!(c.id_user === myId && (c.tip || 'OPERATER') === 'OPERATER') &&
                                                oznakaPrisustva(stanjeKorisnika(c.tip, c.id_user, c.poslednja_aktivnost), { kompaktno: true })}
                                        </div>
                                        {c.uloga === 2 && <span className="chat-info-admin">{t('chat.admin')}</span>}
                                    </div>
                                ))}
                            </div>

                            <div className="chat-info-section">{t('chat.images')} ({chatImages.length})</div>
                            {chatImages.length === 0 ? (
                                <div className="chat-info-empty">{t('chat.noImages')}</div>
                            ) : (
                                <>
                                    <div className="chat-info-media-grid">
                                        {chatImages.slice(0, 6).map((url, i) => (
                                            <img key={i} src={url} alt="" className="chat-info-media-thumb" onClick={() => openImgPreview(url)} />
                                        ))}
                                    </div>
                                    <button className="chat-info-show-all" onClick={() => setShowMediaGallery(true)}>
                                        {t('chat.showAll')} ({chatImages.length})
                                    </button>
                                </>
                            )}
                        </div>
                    )
                )}
            </div>
            {showInfoPanel && <div className="chat-group-panel-overlay" onClick={closeInfoPanel} />}

            {/* ── Meni poruke (desni klik / dugi pritisak) ── */}
            {ctxMeni && (() => {
                const m      = ctxMeni.msg;
                const mojaJe = m.id_korisnik === myId && (m.tip_korisnika || 'OPERATER') === 'OPERATER';
                const tekst  = m.tip === 'text' && !!m.sadrzaj;
                const radnja = (fn) => (e) => { e.stopPropagation(); setCtxMeni(null); fn(); };
                return (
                    <div
                        ref={drziUProzoru}
                        className="chat-ctx-meni"
                        style={{ left: ctxMeni.x, top: ctxMeni.y }}
                        onClick={e => e.stopPropagation()}
                        onContextMenu={e => e.preventDefault()}
                    >
                        <div className="chat-ctx-reakcije">
                            {QUICK_REACTIONS.map(em => (
                                <button key={em} onClick={radnja(() => sendReaction(m.id, m.id_grupe, em))}>{em}</button>
                            ))}
                            <button className="chat-ctx-jos" onClick={(e) => otvoriPunIzbor(e, m)} title={t('chat.josEmotikona')}>
                                <i className="pi pi-plus" />
                            </button>
                        </div>
                        <button className="chat-ctx-stavka" onClick={radnja(() => startReply(m))}>
                            <i className="pi pi-reply" /><span>{t('chat.reply')}</span>
                        </button>
                        {tekst && (
                            <button className="chat-ctx-stavka" onClick={radnja(() => kopirajPoruku(m))}>
                                <i className="pi pi-copy" /><span>{t('chat.kopiraj')}</span>
                            </button>
                        )}
                        {mozeProsledjivanje(m) && (
                            <button className="chat-ctx-stavka" onClick={radnja(() => otvoriProsledi(m))}>
                                <i className="pi pi-share-alt" /><span>{t('chat.prosledi')}</span>
                            </button>
                        )}
                        <button className="chat-ctx-stavka" onClick={radnja(() => sacuvajPoruke([m.id], !m.sacuvana))}>
                            <i className={`pi ${m.sacuvana ? 'pi-star-fill' : 'pi-star'}`} />
                            <span>{m.sacuvana ? t('chat.ukloniIzSacuvanih') : t('chat.sacuvaj')}</span>
                        </button>
                        <button className="chat-ctx-stavka" onClick={radnja(() => pocniIzbor(m))}>
                            <i className="pi pi-check-circle" /><span>{t('chat.izaberi')}</span>
                        </button>
                        {mozeZadatak && (
                            <button className="chat-ctx-stavka" onClick={radnja(() => otvoriZadatak(m))}>
                                <i className="pi pi-check-square" /><span>{t('chat.napraviZadatak')}</span>
                            </button>
                        )}
                        {zakacenSet.has(m.id) ? (
                            <button className="chat-ctx-stavka" onClick={radnja(() => otkaci(m.id))}>
                                <i className="pi pi-thumbtack" /><span>{t('chat.otkaci')}</span>
                            </button>
                        ) : (
                            <button className="chat-ctx-stavka" onClick={radnja(() => zakaci(m))}>
                                <i className="pi pi-thumbtack" /><span>{t('chat.zakaci')}</span>
                            </button>
                        )}
                        {mojaJe && (
                            <button className="chat-ctx-stavka" onClick={radnja(() => otvoriInfo(m, ctxMeni.x, ctxMeni.y))}>
                                <i className="pi pi-info-circle" /><span>{t('chat.infoPoruke')}</span>
                            </button>
                        )}
                        {mojaJe && m.tip === 'text' && (
                            <button className="chat-ctx-stavka" onClick={radnja(() => startEdit(m))}>
                                <i className="pi pi-pencil" /><span>{t('common.edit')}</span>
                            </button>
                        )}
                        {mojaJe && (
                            <button className="chat-ctx-stavka opasno" onClick={radnja(() => deleteMessage(m.id, m.id_grupe))}>
                                <i className="pi pi-trash" /><span>{t('common.delete')}</span>
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* ── Pun izbor emotikona za reakciju ── */}
            {punIzbor && (
                <div
                    ref={drziUProzoru}
                    className="chat-ctx-izbor"
                    style={{ left: punIzbor.x, top: punIzbor.y }}
                    onClick={e => e.stopPropagation()}
                >
                    <Picker
                        data={data}
                        onEmojiSelect={(em) => { sendReaction(punIzbor.msg.id, punIzbor.msg.id_grupe, em.native); setPunIzbor(null); }}
                        theme={theme}
                        locale="en"
                        previewPosition="none"
                        autoFocus
                    />
                </div>
            )}

            {/* ── Pregled prevučenih priloga pre slanja ── */}
            {prilozi && (() => {
                const imaSlika = prilozi.fajlovi.some(f => f.jeSlika);
                const velicina = (b) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
                return (
                    <div className="chat-modal-overlay" onClick={zatvoriPriloge}>
                        <div className="chat-modal chat-prilozi-modal" onClick={e => e.stopPropagation()}
                             onDragOver={naPrevlacenjeIznad} onDrop={(e) => naPustanje(e, prilozi.rezim)}>
                            <div className="chat-modal-header">
                                <h3>{t('chat.prilozi')} · {activeConv?.naziv_prikaz || activeConv?.naziv || ''}</h3>
                                <button className="chat-modal-close" onClick={zatvoriPriloge} disabled={saljePriloge}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>
                            <div className="chat-modal-body">
                                {imaSlika && (
                                    <div className="chat-modal-mode">
                                        <button type="button"
                                            className={`chat-modal-mode-btn${prilozi.rezim === 'kompresovano' ? ' active' : ''}`}
                                            onClick={() => setPrilozi(p => ({ ...p, rezim: 'kompresovano' }))}>
                                            <i className="pi pi-image" />
                                            <span>{t('chat.prevuciKaoSliku')}</span>
                                        </button>
                                        <button type="button"
                                            className={`chat-modal-mode-btn${prilozi.rezim === 'original' ? ' active' : ''}`}
                                            onClick={() => setPrilozi(p => ({ ...p, rezim: 'original' }))}>
                                            <i className="pi pi-file" />
                                            <span>{t('chat.prevuciKaoFajl')}</span>
                                        </button>
                                    </div>
                                )}
                                {imaSlika && (
                                    <div className="chat-prilozi-napomena">
                                        {prilozi.rezim === 'kompresovano' ? t('chat.prevuciKaoSlikuOpis') : t('chat.prevuciKaoFajlOpis')}
                                    </div>
                                )}
                                <div className="chat-prilozi-lista">
                                    {prilozi.fajlovi.map(f => (
                                        <div key={f.id} className="chat-prilog">
                                            {f.preview
                                                ? <img src={f.preview} alt="" className="chat-prilog-slika" />
                                                : <span className="chat-prilog-ikona"><i className="pi pi-file" /></span>}
                                            <div className="chat-prilog-tekst">
                                                <span className="chat-prilog-ime">{f.file.name}</span>
                                                <span className={`chat-prilog-velicina${f.file.size > MAX_VELICINA_MB * 1048576 && !(f.jeSlika && prilozi.rezim === 'kompresovano') ? ' prevelik' : ''}`}>
                                                    {velicina(f.file.size)}
                                                </span>
                                            </div>
                                            <button className="chat-prilog-ukloni" onClick={() => ukloniPrilog(f.id)} disabled={saljePriloge} title={t('chat.remove')}>
                                                <i className="pi pi-times" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="chat-modal-footer">
                                <button className="btn-cancel" onClick={zatvoriPriloge} disabled={saljePriloge}>{t('common.cancel')}</button>
                                <button className="btn-create" onClick={posaljiPriloge} disabled={saljePriloge || !prilozi.fajlovi.length}>
                                    {saljePriloge
                                        ? <i className="pi pi-spin pi-spinner" />
                                        : `${t('chat.send')} (${prilozi.fajlovi.length})`}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Prosledi poruku ── */}
            {prosledi && (() => {
                const q = prosledPretraga.trim().toLowerCase();
                const izvor = prosledi[0];
                const ciljevi = conversations.filter(c =>
                    c.id !== izvor.id_grupe &&
                    (!q || (c.naziv_prikaz || c.naziv || '').toLowerCase().includes(q)));
                const pregled = prosledi.length > 1 ? t('chat.porukaBroj', { broj: prosledi.length })
                              : izvor.tip === 'text' ? (izvor.sadrzaj || '').slice(0, 120)
                              : izvor.tip === 'image' ? '📷 Slika'
                              : izvor.tip === 'gif'   ? '🎬 GIF'
                              : izvor.tip === 'location' ? '📍 Lokacija'
                              : izvor.tip === 'audio' ? '🎤 Glasovna poruka'
                              : izvor.tip === 'video' ? '🎥 Video'
                              : `📎 ${izvor.file_naziv || 'Fajl'}`;
                return (
                    <div className="chat-modal-overlay" onClick={zatvoriProsledi}>
                        <div className="chat-modal" onClick={e => e.stopPropagation()}>
                            <div className="chat-modal-header">
                                <h3>{t('chat.prosledjivanjeNaslov')}</h3>
                                <button className="chat-modal-close" onClick={zatvoriProsledi}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>
                            <div className="chat-modal-body">
                                <div className="chat-prosledi-pregled">
                                    <i className="pi pi-share-alt" />
                                    <span>{pregled}</span>
                                </div>
                                <div className="chat-modal-search">
                                    <input
                                        autoFocus
                                        placeholder={t('chat.searchConvPh')}
                                        value={prosledPretraga}
                                        onChange={e => setProsledPretraga(e.target.value)}
                                    />
                                </div>
                                <div className="chat-modal-user-list">
                                    {ciljevi.length === 0 && (
                                        <div className="chat-gif-empty">{t('chat.noResults')}</div>
                                    )}
                                    {ciljevi.map(c => {
                                        const ime = c.naziv_prikaz || c.naziv || 'Razgovor';
                                        const sel = prosledjivanjeU.includes(c.id);
                                        return (
                                            <div key={c.id} className={`chat-modal-user${sel ? ' selected' : ''}`} onClick={() => prebaciCilj(c.id)}>
                                                <ChatAvatar
                                                    name={ime}
                                                    avatar={c.tip === 2 ? c.grupa_avatar : c.dm_avatar}
                                                    isGroup={c.tip === 2}
                                                    size={36}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div className="chat-modal-user-name">{ime}</div>
                                                    {c.tip === 2 && (
                                                        <div className="chat-modal-user-email">{c.broj_clanova} {t('chat.membersSuffix')}</div>
                                                    )}
                                                </div>
                                                <div className="chat-modal-user-check">
                                                    {sel && <i className="pi pi-check" />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="chat-modal-footer">
                                <button className="btn-cancel" onClick={zatvoriProsledi}>{t('common.cancel')}</button>
                                <button className="btn-create" onClick={posaljiProsledjeno} disabled={!prosledjivanjeU.length}>
                                    {t('chat.prosledi')}{prosledjivanjeU.length > 0 && ` (${prosledjivanjeU.length})`}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Snimci poziva ──
                Isti obrazac kao sacuvane poruke: overlay zatvara klikom pored,
                krstic u zaglavlju. Spisak se ucitava tek kad se modal otvori —
                komponenta se montira tada, pa se zvuk ne vuce bez potrebe. */}
            {snimciOtvoreno && (
                <div className="chat-modal-overlay" onClick={() => setSnimciOtvoreno(false)}>
                    <div className="chat-modal chat-modal-snimci" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3><i className="pi pi-microphone" /> {t('snimci.tab')}</h3>
                            <button className="chat-modal-close" onClick={() => setSnimciOtvoreno(false)}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-modal-body">
                            <SnimciPoziva bezOkvira />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Sačuvane poruke (iz svih razgovora) ── */}
            {sacuvaneOtvoreno && (
                <div className="chat-modal-overlay" onClick={() => setSacuvaneOtvoreno(false)}>
                    <div className="chat-modal" onClick={e => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <h3><i className="pi pi-star-fill chat-sacuvane-naslov-ikona" /> {t('chat.sacuvane')}</h3>
                            <button className="chat-modal-close" onClick={() => setSacuvaneOtvoreno(false)}>
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="chat-modal-body chat-sacuvane-lista">
                            {sacuvaneUcitava && <div className="chat-gif-loading"><i className="pi pi-spin pi-spinner" /></div>}
                            {!sacuvaneUcitava && sacuvane.length === 0 && (
                                <div className="chat-gif-empty">{t('chat.nemaSacuvanih')}</div>
                            )}
                            {!sacuvaneUcitava && sacuvane.map(s => {
                                const pregled = s.tip === 'text' ? (s.sadrzaj || '')
                                              : s.tip === 'image' ? '📷 Slika'
                                              : s.tip === 'gif' ? '🎬 GIF'
                                              : s.tip === 'location' ? '📍 Lokacija'
                                              : s.tip === 'audio' ? '🎤 Glasovna poruka'
                                              : s.tip === 'video' ? '🎥 Video'
                                              : s.tip === 'poll' ? '📊 Anketa'
                                              : s.tip === 'event' ? '📅 Event'
                                              : `📎 ${s.file_naziv || 'Fajl'}`;
                                return (
                                    <div key={s.id} className="chat-sacuvana" onClick={() => idiNaSacuvanu(s)}>
                                        <div className="chat-sacuvana-zaglavlje">
                                            <span className="chat-sacuvana-razgovor">
                                                <i className={`pi ${s.conv_tip === 2 ? 'pi-users' : 'pi-user'}`} /> {s.razgovor || ''}
                                            </span>
                                            <span className="chat-sacuvana-datum">{datumVremeTekst(s.datum)}</span>
                                        </div>
                                        <div className="chat-sacuvana-autor">{s.ime_korisnika}</div>
                                        <div className="chat-sacuvana-tekst">{pregled}</div>
                                        <button
                                            className="chat-sacuvana-ukloni"
                                            onClick={(e) => { e.stopPropagation(); sacuvajPoruke([s.id], false); }}
                                            title={t('chat.ukloniIzSacuvanih')}
                                        >
                                            <i className="pi pi-star-fill" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Napravi zadatak iz poruke ── */}
            {zadatak && (() => {
                const q = zadPretraga.trim().toLowerCase();
                const spisak = zadKorisnici.filter(u => !q
                    || u.label.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
                const prebaci = (id) => setZadatak(z => ({
                    ...z, kome: z.kome.includes(id) ? z.kome.filter(x => x !== id) : [...z.kome, id],
                }));
                return (
                    <div className="chat-modal-overlay" onClick={zatvoriZadatak}>
                        <div className="chat-modal" onClick={e => e.stopPropagation()}>
                            <div className="chat-modal-header">
                                <h3>{t('chat.napraviZadatak')}</h3>
                                <button className="chat-modal-close" onClick={zatvoriZadatak}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>
                            <div className="chat-modal-body">
                                <textarea
                                    className="chat-modal-input chat-zadatak-tekst"
                                    value={zadatak.tekst}
                                    onChange={e => setZadatak(z => ({ ...z, tekst: e.target.value }))}
                                    rows={5}
                                    maxLength={2000}
                                    autoFocus
                                />
                                {zadatak.msg.tip === 'image' && zadatak.msg.file_path && (
                                    <div className="chat-zadatak-prilog">
                                        <img src={fileUrl(zadatak.msg.file_path)} alt="" />
                                        <span>{t('chat.zadatakSlikaPrilog')}</span>
                                    </div>
                                )}

                                <label className="chat-modal-label" style={{ marginTop: 12 }}>{t('chat.zadatakPrioritet')}</label>
                                <div className="chat-modal-mode">
                                    {zadPrioriteti.map(p => (
                                        <button
                                            key={p.value}
                                            type="button"
                                            className={`chat-modal-mode-btn${zadatak.prioritet === p.value ? ' active' : ''}`}
                                            onClick={() => setZadatak(z => ({ ...z, prioritet: p.value }))}
                                        >
                                            <span>{p.label}</span>
                                        </button>
                                    ))}
                                </div>

                                <label className="chat-modal-label" style={{ marginTop: 12 }}>
                                    {t('chat.zadatakKome')}{zadatak.kome.length > 0 && ` (${zadatak.kome.length})`}
                                </label>
                                <div className="chat-modal-search">
                                    <input
                                        placeholder={t('chat.searchUsersPh')}
                                        value={zadPretraga}
                                        onChange={e => setZadPretraga(e.target.value)}
                                    />
                                </div>
                                <div className="chat-modal-user-list chat-zadatak-lista">
                                    {spisak.map(u => {
                                        const sel = zadatak.kome.includes(u.value);
                                        return (
                                            <div key={u.value} className={`chat-modal-user${sel ? ' selected' : ''}`} onClick={() => prebaci(u.value)}>
                                                <ChatAvatar name={u.label} avatar={u.avatar} size={32} />
                                                <div style={{ flex: 1 }}>
                                                    <div className="chat-modal-user-name">{u.label}</div>
                                                    <div className="chat-modal-user-email">{u.email}</div>
                                                </div>
                                                <div className="chat-modal-user-check">
                                                    {sel && <i className="pi pi-check" />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="chat-modal-footer">
                                <button className="btn-cancel" onClick={zatvoriZadatak} disabled={zadSalje}>{t('common.cancel')}</button>
                                <button
                                    className="btn-create"
                                    onClick={posaljiZadatak}
                                    disabled={zadSalje || !zadatak.tekst.trim() || !zadatak.kome.length || !zadatak.prioritet}
                                >
                                    {zadSalje ? <i className="pi pi-spin pi-spinner" /> : t('chat.napraviZadatak')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Readers popup ── */}
            {readersPopup.open && (
                <div
                    className="chat-readers-popup"
                    /* Crta se iznad tačke; pri vrhu ekrana (otvoreno iz menija na
                       gornjoj poruci) ispod nje, da ne ode preko ivice. */
                    style={readersPopup.y < 320
                        ? { top: readersPopup.y + 8, transform: 'none' }
                        : { top: readersPopup.y - 8 }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="chat-readers-popup-header">
                        {t('chat.whoRead')}
                        <button onClick={closeReadersPopup}><i className="pi pi-times" /></button>
                    </div>
                    {readersPopup.loading ? (
                        <div className="chat-readers-popup-loading"><i className="pi pi-spin pi-spinner" /></div>
                    ) : (
                        <div className="chat-readers-popup-list">
                            <div className="chat-readers-popup-section">{t('chat.procitali')} · {readersPopup.list.length}</div>
                            {readersPopup.list.length === 0 && (
                                <div className="chat-readers-popup-empty">{t('chat.noOneRead')}</div>
                            )}
                            {readersPopup.list.map(r => (
                                <div key={`${r.tip || 'OPERATER'}:${r.id}`} className="chat-readers-popup-row">
                                    <ChatAvatar name={r.ime} avatar={r.avatar} size={30} style={{ flexShrink: 0 }} onExpand={openImgPreview} />
                                    <div className="chat-readers-popup-info">
                                        <span className="chat-readers-popup-name">{r.ime}</span>
                                        <span className="chat-readers-popup-time">{formatTime(r.datum)}</span>
                                    </div>
                                </div>
                            ))}
                            <div className="chat-readers-popup-section">{t('chat.nisuProcitali')} · {readersPopup.nisu.length}</div>
                            {readersPopup.nisu.length === 0 && (
                                <div className="chat-readers-popup-empty">{t('chat.sviProcitali')}</div>
                            )}
                            {readersPopup.nisu.map(r => (
                                <div key={`${r.tip || 'OPERATER'}:${r.id}`} className="chat-readers-popup-row nije">
                                    <ChatAvatar name={r.ime} avatar={r.avatar} size={30} style={{ flexShrink: 0 }} onExpand={openImgPreview} />
                                    <div className="chat-readers-popup-info">
                                        <span className="chat-readers-popup-name">{r.ime}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Image preview overlay ── */}
            {imgPreview && (
                <div className="chat-img-overlay" ref={slojSlikeRef} onClick={closeImgPreview}
                     onMouseMove={naPomerajSlike} onMouseUp={naOtpustSlike} onMouseLeave={naOtpustSlike}>
                    {/* Radnje nad slikom. Stoje uz dugme za zatvaranje, a klik se
                        zaustavlja da ne zatvori pregled. */}
                    <div className="chat-img-overlay-akcije" onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={kopirajSliku} title={t('chat.kopiraj') + ' (Ctrl+C)'}>
                            <i className="pi pi-copy" />
                        </button>
                        <button type="button" onClick={skiniSliku} title={t('chat.skini')}>
                            <i className="pi pi-download" />
                        </button>
                    </div>
                    <button className="chat-img-overlay-close" onClick={closeImgPreview}>
                        <i className="pi pi-times" />
                    </button>
                    {previewIndex > 0 && (
                        <button className="chat-img-overlay-nav chat-img-overlay-prev" onClick={(e) => { e.stopPropagation(); showPrevImg(); }}>
                            <i className="pi pi-chevron-left" />
                        </button>
                    )}
                    <img src={imgPreview} alt="preview"
                         className={zum > 1 ? 'uvecana' : ''}
                         style={{ transform: `translate(${pomak.x}px, ${pomak.y}px) scale(${zum})` }}
                         onClick={e => e.stopPropagation()}
                         onMouseDown={naPritisakSlike}
                         onDoubleClick={e => { e.stopPropagation(); setZum(1); setPomak({ x: 0, y: 0 }); }}
                         draggable={false} />
                    {previewIndex !== null && previewIndex < chatImages.length - 1 && (
                        <button className="chat-img-overlay-nav chat-img-overlay-next" onClick={(e) => { e.stopPropagation(); showNextImg(); }}>
                            <i className="pi pi-chevron-right" />
                        </button>
                    )}
                </div>
            )}
        </div>
        </>
    );
};

export default Chat;
